<template>
  <!-- 
      PvP遊戲多點觸控優化樣式：
      - touch-action: none        → 禁用瀏覽器預設觸控手勢（縮放、滾動），確保多點觸控正常
      - user-select: none         → 防止觸控時選取文字，避免干擾遊戲
      - -webkit-touch-callout     → 禁用 iOS 長按選單（複製、貼上等）
      - -webkit-user-select       → iOS Safari 舊版兼容性，防止文字選取
  -->
  <div class="pvpgame-container" style="touch-action: manipulation; user-select: none; -webkit-touch-callout: none; -webkit-user-select: none;">
    <!-- 小螢幕提示遮罩 -->
    <div v-if="showSmallScreenWarning && !isWarningDismissed" class="small-screen-warning">
      <div class="warning-content">
        <svg-icon icon-class="info" class="warning-icon" />
        <p class="warning-text">{{ $t('PvPgame.smallScreenWarning') || '本遊戲為桌面版介面，建議於電腦平板使用，且最低螢幕寬度需求為 1200px*800px' }}</p>
        <button class="warning-close" @click="dismissWarning">
          <svg-icon icon-class="close" />
        </button>
      </div>
    </div>

    <!-- 游戏区域 -->
    <div class="game-area" ref="gameAreaRef"
      :style="{ 'background': mockImg, 'background-size': 'cover', 'background-repeat': 'no-repeat' }">
     

      <div class="game-header">
        <div class="game-topic">{{ gameConfig.topic }}</div>
      </div>
      <div class="timer">{{ formatTime(timeRemaining) }}
        <button class="start-button" @click="toggleGame">
          <svg-icon :icon-class="gameStarted ? (gamePaused ? 'play' : 'pause') : 'play'" />
        </button>
      </div>

      <!-- 遊戲開始前彈窗區 -->
      <div v-if="beforeGame && (playerCount !== null || !isFromActId)" class="before-game-container">
        <!-- 正式活動模式：三欄佈局（雙人模式）或兩欄佈局（單人模式） -->
        <div v-if="isFromActId" class="actid-mode-wrapper" :class="{ 'dual-player-layout': !isSinglePlayerMode }">
          <div class="actid-mode-modal" :class="{ 'dual-player-layout': !isSinglePlayerMode }">
            <!-- 左側：左側玩家資訊（僅雙人模式顯示） -->
            <div v-if="!isSinglePlayerMode" class="student-info-left">
              <h4 class="student-info-title">{{ $t('PvPgame.leftPlayer') }}</h4>

              <!-- HiTeach 名單下拉選單模式 -->
              <template v-if="classRosterSource === 'HT' && classRoster.length > 0">
                <div class="form-group">
                  <select
                    @change="handleLeftRosterSelection"
                    class="student-input student-select"
                  >
                    <option value="">{{ $t('gameActManager.selectStudent') }}</option>
                    <option
                      v-for="member in classRoster"
                      :key="`left-${member.memberName}-${member.seatID}`"
                      :value="`${member.memberName}-${member.seatID}`"
                    >
                      {{ member.seatID }} - {{ member.memberName }}
                    </option>
                  </select>
                  <span v-if="leftStudentInfoErrors.name" class="error-text">{{ leftStudentInfoErrors.name }}</span>
                </div>

                <!-- 顯示已選擇的資訊（唯讀） -->
                <div v-if="leftStudentInfo.name" class="selected-info">
                  <div class="info-item">
                    <span class="info-label">{{ $t('gameActManager.seatNumber') }}:</span>
                    <span class="info-value">{{ leftStudentInfo.seatNumber }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">{{ $t('gameActManager.studentName') }}:</span>
                    <span class="info-value">{{ leftStudentInfo.name }}</span>
                  </div>
                </div>
              </template>

              <!-- 手動輸入模式（預設） -->
              <template v-else>
                <div class="form-group">
                  <input
                    v-model="leftStudentInfo.seatNumber"
                    type="text"
                    :placeholder="$t('gameActManager.seatNumberPlaceholder')"
                    maxlength="3"
                    class="student-input"
                  />
                  <span v-if="leftStudentInfoErrors.seatNumber" class="error-text">{{ leftStudentInfoErrors.seatNumber }}</span>
                </div>
                <div class="form-group">
                  <input
                    v-model="leftStudentInfo.name"
                    type="text"
                    :placeholder="$t('gameActManager.studentNamePlaceholder')"
                    maxlength="20"
                    class="student-input"
                  />
                  <span v-if="leftStudentInfoErrors.name" class="error-text">{{ leftStudentInfoErrors.name }}</span>
                </div>
              </template>
            </div>

            <!-- 中間：遊戲資訊和播放按鈕 -->
            <div class="game-info-center">
              <div class="game-title">{{ gameConfig.topic }}</div>
              <div class="game-info">
                <div class="game-time">{{ $t('PvPgame.gameDuration') }}： {{ gameConfig.gameDuration }} {{ $t('PvPgame.second') }}</div>
                <div class="game-speed">{{ $t('PvPgame.gameSpeed') }}： {{ getSpeedText(gameConfig.gameSpeed) }}</div>
              </div>
              <!-- 按鈕區 -->
              <div class="center-buttons">
                <button class="action-button play-button play-in-center" @click="showReadyGoCountdown()">
                  <svg-icon icon-class="play" class="play-icon" />
                </button>
              </div>
            </div>

            <!-- 右側：右側玩家資訊 -->
            <div class="student-info-right">
              <h4 class="student-info-title">{{ isSinglePlayerMode ? $t('gameActManager.enterStudentInfo') : $t('PvPgame.rightPlayer') }}</h4>

              <!-- HiTeach 名單下拉選單模式 -->
              <template v-if="classRosterSource === 'HT' && classRoster.length > 0">
                <div class="form-group">
                  <select
                    @change="handleRightRosterSelection"
                    class="student-input student-select"
                  >
                    <option value="">{{ $t('gameActManager.selectStudent') }}</option>
                    <option
                      v-for="member in classRoster"
                      :key="`right-${member.memberName}-${member.seatID}`"
                      :value="`${member.memberName}-${member.seatID}`"
                    >
                      {{ member.seatID }} - {{ member.memberName }}
                    </option>
                  </select>
                  <span v-if="rightStudentInfoErrors.name" class="error-text">{{ rightStudentInfoErrors.name }}</span>
                </div>

                <!-- 顯示已選擇的資訊（唯讀） -->
                <div v-if="rightStudentInfo.name" class="selected-info">
                  <div class="info-item">
                    <span class="info-label">{{ $t('gameActManager.seatNumber') }}:</span>
                    <span class="info-value">{{ rightStudentInfo.seatNumber }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">{{ $t('gameActManager.studentName') }}:</span>
                    <span class="info-value">{{ rightStudentInfo.name }}</span>
                  </div>
                </div>
              </template>

              <!-- 手動輸入模式（預設） -->
              <template v-else>
                <div class="form-group">
                  <input
                    v-model="rightStudentInfo.seatNumber"
                    type="text"
                    :placeholder="$t('gameActManager.seatNumberPlaceholder')"
                    maxlength="3"
                    class="student-input"
                  />
                  <span v-if="rightStudentInfoErrors.seatNumber" class="error-text">{{ rightStudentInfoErrors.seatNumber }}</span>
                </div>
                <div class="form-group">
                  <input
                    v-model="rightStudentInfo.name"
                    type="text"
                    :placeholder="$t('gameActManager.studentNamePlaceholder')"
                    maxlength="20"
                    class="student-input"
                  />
                  <span v-if="rightStudentInfoErrors.name" class="error-text">{{ rightStudentInfoErrors.name }}</span>
                </div>
              </template>
            </div>
          </div>

          <!-- 切換遊戲模板區塊（僅在單人模式且允許切換時顯示） -->
          <div v-if="isSinglePlayerMode && allowTemplateChange" class="game-template-switcher">
            <div class="switcher-title">{{ $t('gameActManager.switchGameTemplate') }}</div>
            <div class="switcher-content">
              <select v-model="selectedGameTemplate" class="template-select">
                <option value="mole">{{ $t('gameActManager.moleGame') }}</option>
                <option value="star">{{ $t('gameActManager.starGame') }}</option>
                <option value="snake">{{ $t('gameActManager.snakeGame') }}</option>
              </select>
              <button class="goto-button" @click="navigateToSelectedGame" :disabled="selectedGameTemplate === 'star'">
                <svg-icon icon-class="arrowright" />
              </button>
            </div>
          </div>
        </div>

        <!-- Demo/WebView 模式：原有佈局 -->
        <div v-else class="before-game-modal">
          <div class="game-title">{{ gameConfig.topic }}</div>
          <div class="game-info">
            <div class="game-time">{{ $t('PvPgame.gameDuration') }}： {{ gameConfig.gameDuration }} {{ $t('PvPgame.second') }}</div>
            <div class="game-speed">{{ $t('PvPgame.gameSpeed') }}： {{ getSpeedText(gameConfig.gameSpeed) }}</div>
          </div>

          <!-- 推送後顯示短網址 -->
          <div v-if="pushedShortUrl" class="url-wrap url-wrap-centered">
              <span class="url-title">URL：</span><span style="color: #2B8EE0; font-size: 14px; font-weight: bold;">{{ pushedShortUrlHost }}</span><span class="short-url">{{ pushedShortCode }}</span>
              <span class="copy-btn" @click="copyPushedShortUrl">
                  <svg-icon icon-class="copy" />
              </span>
              <span class="qrcode-btn" @click="showPushedQRCode">
                  <svg-icon icon-class="qrcode-new" />
              </span>
              <span v-if="isWebViewOrIframe && !isPreviewMode" class="repush-btn" @click="repush" :title="$t('Game.repush') || '重新推送'">
                  <svg-icon icon-class="upload" class="repush-icon" />
              </span>
          </div>
          <!-- QR Code overlay -->
          <div v-if="showQRCodeOverlay" class="qrcode-overlay" @click.self="closeQRCodeOverlay">
              <div class="qrcode-modal">
                  <button class="qrcode-close-btn" @click="closeQRCodeOverlay">×</button>
                  <canvas ref="qrcodeCanvas" class="qrcode-canvas"></canvas>
                  <div style="margin-top: 10px; background-color: #fff9c4; display: flex; align-items: center; justify-content: center; gap: 6px; padding: 10px; border-radius: 5px; white-space: nowrap; font-weight: bold;">
                      <span style="color: #333; font-size: 14px;">URL：</span><span style="color: #2B8EE0; font-size: 14px; font-weight: bold;">{{ pushedShortUrlHost }}</span><span style="color: #000; font-size: 26px; background-color: #fff; padding: 4px 6px; border-radius: 3px; font-weight: bold;">{{ pushedShortCode }}</span>
                  </div>
              </div>
          </div>

          <!-- 按鈕區 -->
          <div class="game-buttons">
            <button class="action-button play-button" @click="showReadyGoCountdown()">
              <svg-icon icon-class="play" class="play-icon"/>
            </button>
            <!-- Demo/WebView 模式下顯示設定按鈕 -->
            <button v-if="!hasConfigUrl && !isPreviewMode" class="action-button setting-button" @click="goToSettings()">
              <svg-icon icon-class="setting-game" class="setting-icon"/>
            </button>
            <!-- 推送按鈕：在 WebView 或 iframe 環境中顯示（恢復模式下可能 isLoggedIn 尚未更新） -->
            <button v-if="isWebViewOrIframe && !isPreviewMode && (isLoggedIn || isRestoredMode) && !pushedActivityId" class="action-button push-button"
                    @click="handleSendClick()">
              <svg-icon icon-class="upload" class="sendweb-icon" />
            </button>
            <!-- ✨ 查看成績按鈕：需校園版授權，在 WebView 或 iframe 環境中顯示，且已推送活動時可用 -->
            <button v-if="isWebViewOrIframe && !isPreviewMode && pushedActivityId && schoolVers"
                    class="action-button view-scores-button"
                    @click="openScoresPanel"
                    :title="$t('HTwbActKitHome.viewScores')">
              <svg-icon icon-class="clipboard-list" class="scores-icon" />
            </button>
            <!-- 雲端管理按鈕：推送後顯示，點擊導向遊戲雲端管理  需校園版授權，-->
            <button v-if="isWebViewOrIframe && !isPreviewMode && pushedActivityId && schoolVers"
                    class="action-button cloud-manage-button"
                    @click="openCloudManagement"
                    :title="$t('HTwbActKitHome.cloudGameManagement')">
              <svg-icon icon-class="cloud-home" class="cloud-manage-icon" />
            </button>
          </div>
        </div>
      </div>


      <!-- Ready, Go! 提示 -->
      <div v-if="showReadyGo" class="ready-go-container">
        <div class="ready-go-text">{{ readyGoText }}</div>
      </div>

      <!-- 遊戲暫停彈窗 -->
      <div v-if="gameStarted && gamePaused && !gameOver" class="pause-game-container">
        <div class="pause-game-modal">
          <div class="pause-title">{{ $t('PvPgame.gamePaused') || '活動暫停' }}</div>
          <div class="pause-buttons">
            <button class="action-button continue-button" @click="resumeGame()">
              <svg-icon icon-class="play"  class="play-icon"/>
            </button>
            <button class="action-button restart-button" @click="resetGame()">
              <svg-icon icon-class="refresh" class="refresh-icon" />
            </button>
          </div>
        </div>
      </div>

      <!-- 單人模式：分數置中顯示 -->
      <div v-if="isSinglePlayerMode" class="center-score">{{ leftScore }}</div>

      <!-- 遊戲區域 -->
      <div class="game-side left-side" :class="{ 'single-player-mode': isSinglePlayerMode }">
        <!-- 雙人模式才顯示左側分數 -->
        <div v-if="!isSinglePlayerMode" class="left-score">{{ leftScore }}</div>
        <div v-for="(item, index) in leftItems" :key="'left-' + index" class="falling-item left-item"
          :style="{ left: item.x + 'px', top: item.y + 'px', backgroundImage: leftBackground }"
          @click="handleItemClick(item, 'left')"
          @touchstart="handleTouchStart($event, item, 'left')"
          @touchend="handleTouchEnd($event)">
          {{ item.text }}
        </div>
      </div>

      <!-- 右侧游戏区域（單人模式隱藏） -->
      <div v-if="!isSinglePlayerMode" class="game-side right-side">
        <div class="right-score">{{ rightScore }}</div>
        <div v-for="(item, index) in rightItems" :key="'right-' + index" class="falling-item right-item"
          :style="{ left: item.x + 'px', top: item.y + 'px', backgroundImage: rightBackground }"
          @click="handleItemClick(item, 'right')"
          @touchstart="handleTouchStart($event, item, 'right')"
          @touchend="handleTouchEnd($event)">
          {{ item.text }}
        </div>
      </div>

      <!-- 預覽標記（右下角） -->
      <div v-if="isPreviewMode" class="preview-badge">{{ $t('gameActManager.previewMode') }}</div>

      <!-- 飛行分數動畫 -->
      <transition-group name="flying-score" tag="div">
        <div
          v-for="effect in flyingScoreEffects"
          :key="effect.id"
          class="flying-score-effect"
          :style="{
            left: effect.currentX + 'px',
            top: effect.currentY + 'px',
            color: effect.isCorrect ? 'blue' : 'red',
            opacity: effect.opacity
          }"
        >
          {{ effect.text }}
        </div>
      </transition-group>

    <!-- 遊戲結束彈窗 -->
    <div v-if="gameOver" class="game-over-modal">
      <div class="game-result-container" :class="{ 'single-player-result': isSinglePlayerMode }" v-show="!showAnswerModal">
        <!-- 左側結果卡片 -->
        <div class="result-card-wrap">
          <div class="win-act" v-show="!isSinglePlayerMode && leftScore > rightScore">
            <img src="@/assets/HTwbActKitCSS/shine.png" alt="">
          </div>
        <div class="result-card left-result" >
          <div class="result-header">
            <!-- 單人模式顯示「遊戲結束」，雙人模式顯示勝負 -->
            <div v-if="isSinglePlayerMode" class="result-title single-player-title">
              {{ $t('PvPgame.gameOver') }}
            </div>
            <div v-else class="result-title" :class="{ 'win-title': leftScore > rightScore, 'tie-title': leftScore === rightScore, 'lose-title': leftScore < rightScore   }">
              {{ leftScore > rightScore ? $t('PvPgame.win') : (leftScore === rightScore ? $t('PvPgame.tie') : '') }}
            </div>
          </div>
          <div class="result-score">
            <div class="star-score">
              <span class="score-number">{{ leftScore }}</span>
            </div>
          </div>
          <div class="result-details">
            <div class="detail-item">
              {{ $t('PvPgame.correctItems') }}: {{ leftCorrectCount }} {{ $t('PvPgame.items') }}
            </div>
            <div class="detail-item">
              {{ $t('PvPgame.errorItems') }}: {{ leftWrongCount }} {{ $t('PvPgame.items') }}
            </div>
            <!-- <div class="detail-item">
              {{ $t('PvPgame.maxCombo') }}: {{ leftMaxCombo }} {{ $t('PvPgame.items') }}
            </div> -->
            <!-- 排名資訊（僅在成績提交後有回傳時顯示） -->
            <div v-if="leftSubmitResult" class="rank-info">
              <div class="detail-item">
                {{ $t('PvPgame.currentRank') }}: {{ leftSubmitResult.rank }}
              </div>
              <div class="detail-item">
                {{ $t('PvPgame.totalParticipants') }}: {{ leftSubmitResult.totalCount }}
              </div>
            </div>
          </div>
        </div>
        </div>

        <!-- 右側結果卡片（單人模式隱藏） -->
         <div v-if="!isSinglePlayerMode" class="result-card-wrap" v-show="!showAnswerModal">
          <div class="win-act" v-show="leftScore < rightScore">
            <img src="@/assets/HTwbActKitCSS/shine.png" alt="">
          </div>
        <div class="result-card right-result" >

          <div class="result-header">
            <div class="result-title" :class="{ 'win-title': rightScore > leftScore, 'tie-title': rightScore === leftScore, 'lose-title': rightScore < leftScore }">
              {{ rightScore > leftScore ? $t('PvPgame.win') : (rightScore === leftScore ? $t('PvPgame.tie') : '') }}
            </div>
          </div>
          <div class="result-score">
            <div class="star-score">
              <span class="score-number">{{ rightScore }}</span>
            </div>
          </div>
          <div class="result-details">
            <div class="detail-item">
              {{ $t('PvPgame.correctItems') }}: {{ rightCorrectCount }} {{ $t('PvPgame.items') }}
            </div>
            <div class="detail-item">
              {{ $t('PvPgame.errorItems') }}: {{ rightWrongCount }} {{ $t('PvPgame.items') }}
            </div>
            <!-- <div class="detail-item">
              {{ $t('PvPgame.maxCombo') }}: {{ rightMaxCombo }} {{ $t('PvPgame.items') }}
            </div> -->
            <!-- 排名資訊（僅在成績提交後有回傳時顯示） -->
            <div v-if="rightSubmitResult" class="rank-info">
              <div class="detail-item">
                {{ $t('PvPgame.currentRank') }}: {{ rightSubmitResult.rank }}
              </div>
              <div class="detail-item">
                {{ $t('PvPgame.totalParticipants') }}: {{ rightSubmitResult.totalCount }}
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>
      <!-- 答案彈窗 -->
      <div v-if="showAnswerModal" class="answer-modal-mask">
          <div class="answer-modal-card">
            <div class="answer-modal-header">
              <span class="answer-modal-title">{{ $t('PvPgame.referenceAnswers') }}</span>
              <button class="answer-modal-close" @click="toggleAnswerModal">×</button>
            </div>
            <div class="answer-modal-section">
              <div class="answer-label correct">✔</div>
              <div class="answer-list">
                <span v-for="(ans, idx) in gameConfig.correctVocabularyList" :key="'c-'+idx" class="answer-item correct">{{ ans }}</span>
              </div>
            </div>
            <div class="answer-modal-section">
              <div class="answer-label wrong">✘</div>
              <div class="answer-list">
                <span v-for="(ans, idx) in gameConfig.errorVocabularyList" :key="'e-'+idx" class="answer-item wrong">{{ ans }}</span>
              </div>
            </div>
          </div>
        </div>

      <!-- 底部按鈕區 -->
      <div class="game-result-actions" v-show="!showAnswerModal">
        <button class="action-button answer-button" @click="toggleAnswerModal">
          <span class="answer-text">{{ $t('PvPgame.showAnswers') }}</span>
        </button>
        <button class="action-button restart-button" @click="resetGame">
          <svg-icon icon-class="refresh" class="refresh-icon" />
        </button>
      </div>
    </div>
  </div>
    </div>

    <!-- ✨ 新增：成績查看面板 -->
    <ActivityScoresPanel
      v-if="showScoresPanel"
      :activity-id="pushedActivityId"
      :activity-name="pushedActivityName"
      :roster-source="classRosterSource || 'general'"
      @back="closeScoresPanel"
    />

    <!-- 推送確認彈窗 -->
    <PushConfirmModal
      v-model="showPushConfirm"
      :activity-name="gameConfig.topic"
      @confirm="executePush"
    />
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted, computed, getCurrentInstance, inject, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { Loading } from 'quasar';
import SvgIcon from '@/components/SvgIcon.vue';
import ActivityScoresPanel from '@/GameActManager/components/ActivityScoresPanel.vue';
import PushConfirmModal from '@/GameActManager/components/PushConfirmModal.vue';
import gameActivityAPI from '@/api/gameActivityAPI';
import api from '@/api';
import jwt_decode from 'jwt-decode';
import { validateAndRedirectGame } from '@/utils/gameActivityRouter';
import { useGameMessageBridge } from '@/composable/useGameMessageBridge';
import { useGameActivityPush } from '@/composable/useGameActivityPush';
import { useHiTeachVersion } from '@/composable/useHiTeachVersion';
import { useSchoolVersion } from '@/composable/useSchoolVersion';
import QRCode from 'qrcode';

const router = useRouter();
const that = getCurrentInstance().proxy;
const { t, locale } = useI18n();
const iviewMessage = inject("$Message");

// HiTeach 版本管理
const { htVersion, isNewPushFuncVersion, loadVersion } = useHiTeachVersion('HTpvpGamePlay');

// 校園版授權判斷
const { schoolVers } = useSchoolVersion();

// 使用遊戲訊息橋接器 Composable（處理 Init.GameHome 和使用者驗證）
useGameMessageBridge('HTpvpGamePlay');

// 根據速度值獲取對應的語系文字
const getSpeedText = (speed) => {
  if (speed === 3) return that.$t('PvPgame.speedFast');
  if (speed === 2) return that.$t('PvPgame.speedMedium');
  if (speed === 1) return that.$t('PvPgame.speedSlow');
  return speed;
};

// 音效參考
// 背景音樂
const backgroundMusic = ref(null);
// 正確音效
const correctSound = ref(null);
// 錯誤音效
const wrongSound = ref(null);
// 平局音效
const tieSound = ref(null);
// 獲勝音效
const winSound = ref(null);
// Ready 音效
const readySound = ref(null);
// Go 音效
const goSound = ref(null);

// 初始化音效（與 TF 遊戲一致的素材與音量設定）
function initSounds() {
  try {
    if (!correctSound.value) {
      correctSound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/shine-6-268910.mp3'));
      correctSound.value.volume = 0.6;
    }
    if (!wrongSound.value) {
      wrongSound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/wrong-answer-129254.mp3'));
      wrongSound.value.volume = 0.6;
    }
    if (!tieSound.value) {
      tieSound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/game-176807.mp3'));
      tieSound.value.volume = 0.7;
    }
    if (!winSound.value) {
      winSound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/orchestral-win-331233.mp3'));
      winSound.value.volume = 0.7;
    }
    if (!readySound.value) {
      readySound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/freeTTS-ready.mp3'));
      readySound.value.volume = 1;
    }
    if (!goSound.value) {
      goSound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/freeTTS-go.mp3'));
      goSound.value.volume = 1;
      goSound.value.currentTime = 0;
    }
  } catch (e) {
    console.warn('[PvP Star] 初始化音效失敗:', e);
  }
}

// 僅供 Demo/WebView 使用的摘星星專用 BGM（不影響 actId）
function initStarDemoBGM() {
  try {
    if (!backgroundMusic.value) {
      backgroundMusic.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/happy-go-lucky-full-length-201627.mp3'));
      backgroundMusic.value.loop = true;
      backgroundMusic.value.volume = 0.6;
    }
  } catch (e) {
    console.warn('[PvP Star] 初始化 Demo BGM 失敗:', e);
  }
}

// 通信工具類
const MessageBridge = {
    // 環境類型
    ENV: {
        WEBVIEW: 'webview',
        WEB: 'web'
    },
    // 當前環境
    currentEnv: 'webview',
    // 初始化
    init() {
        this.currentEnv = window?.chrome?.webview ? this.ENV.WEBVIEW : this.ENV.WEB;
        return this;
    },
    // 發送消息
    postMessage(data, origin = '*') {
        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.postMessage(data);
        } else {
            // 先發送給自己（當前 window）
            window.postMessage(data, origin);

            // 如果在 iframe 內，再同步發送給父層 window
            try {
                if (window.parent && window.parent !== window) {
                    window.parent.postMessage(data, origin);
                }
            } catch (e) {
                // 跨域時可能會丟例外，忽略即可
            }
        }
    },
    // 添加消息監聽器
    addEventListener(callback) {
        const wrappedCallback = (event) => callback(event);
        // 將包裝後的回調存儲起來，以便後續移除
        if (!this._listeners) {
            this._listeners = new WeakMap();
        }
        this._listeners.set(callback, wrappedCallback);

        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.addEventListener('message', wrappedCallback);
        } else {
            window.addEventListener('message', wrappedCallback);
        }
    },
    // 移除消息監聽器
    removeEventListener(callback) {
        if (!this._listeners) {
            return;
        }
        const wrappedCallback = this._listeners.get(callback);
        if (!wrappedCallback) {
            return;
        }

        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.removeEventListener('message', wrappedCallback);
        } else {
            window.removeEventListener('message', wrappedCallback);
        }
        this._listeners.delete(callback);
    }
};

// 初始化消息橋接器
const messageBridge = MessageBridge.init();

// WebView 環境檢測
const isWebView = computed(() => {
    return messageBridge.currentEnv === MessageBridge.ENV.WEBVIEW;
});

// iframe 環境檢測
const isInIframe = computed(() => {
    try {
        return window.self !== window.top;
    } catch (e) {
        return true; // 如果拋出異常，說明在跨域 iframe 中
    }
});

// WebView 或 iframe 環境（用於顯示推送與檢視成績按鈕）
const isWebViewOrIframe = computed(() => {
    return isWebView.value || isInIframe.value;
});

// 判斷是否應該使用新版 Push 功能（需校園版授權 + iframe 環境或 HiTeach 版本支援）
const shouldUseNewPushFunc = computed(() => {
    if (!schoolVers.value) return false;
    return isNewPushFuncVersion.value || isInIframe.value;
});

// 推送按鈕冷卻狀態
const isPushing = ref(false);

// ✨ 成績查看面板相關狀態
const showScoresPanel = ref(false);
const pushedActivityId = ref('');  // 儲存最新推送的活動 ID
const pushedActivityName = ref(''); // 儲存活動名稱
const pushedShortUrl = ref(''); // 儲存最新推送的完整短網址（如 https://hiteach.cc/abc123）
const showQRCodeOverlay = ref(false);
const qrcodeCanvas = ref(null);

// 從完整短網址拆出 host 與 code（如 "https://hiteach.cc/" 與 "abc123"）
const pushedShortUrlHost = computed(() => {
    if (!pushedShortUrl.value) return '';
    const match = pushedShortUrl.value.match(/^(https?:\/\/[^/]+\/)/);
    return match ? match[1] : '';
});
const pushedShortCode = computed(() => {
    if (!pushedShortUrl.value || !pushedShortUrlHost.value) return pushedShortUrl.value;
    return pushedShortUrl.value.slice(pushedShortUrlHost.value.length);
});

// 推送確認彈窗狀態
const showPushConfirm = ref(false);

// 監聽器引用（用於清理）
let messageHandlerRef = null;

// 短網址域名配置
// 短網址域名配置（已移至 useGameActivityPush composable 中）
// const shortURLHost = computed(() => {
//     return locale.value === 'zh-CN' ? 'hiteach.cn' : 'hiteach.cc';
// });

// 使用統一的遊戲活動推送 composable
const { createAndPushShortUrl, isLoggedIn } = useGameActivityPush({
    gameName: 'star',
    gameType: 'pvpgame',
    gameRoute: '/ht-pvpgame-play',
    questionType: 'recognition',
    messageBridge: messageBridge,
    htVersion: htVersion,
    serializeGameConfig: (config) => {
        // 推送時將 correctVocabularyList 對應回 DB 使用的 vocabularyList
        const payload = { ...config };
        if (payload.correctVocabularyList !== undefined) {
            payload.vocabularyList = payload.correctVocabularyList;
            delete payload.correctVocabularyList;
        }
        return JSON.stringify(payload);
    }
});

// 配置加載狀態
const hasConfigUrl = ref(false);
const configUrl = ref('');

// 預覽模式相關
const isPreviewMode = ref(false);
const playerCount = ref(null); // 初始值為 null,等待 API 載入

// 判斷是否為單人模式（支援預覽模式和 actId 模式）
const isSinglePlayerMode = computed(() => {
  return playerCount.value === 1;
});

// 遊戲模板切換相關
const hasGameSwitched = ref(false); // 是否已經切換過遊戲
const allowTemplateChange = ref(true); // 活動是否允許切換遊戲模板（從 API 讀取）
const selectedGameTemplate = ref('star'); // 當前選擇的遊戲模板（預設為摘星星）

// 正式活動模式相關
const isFromActId = ref(false);

// 小螢幕警告相關
const windowWidth = ref(window.innerWidth);
const showSmallScreenWarning = computed(() => windowWidth.value < 1200);
const isWarningDismissed = ref(false);

// 恢復模式相關（教師端 iframe 翻頁恢復）
const isRestoredMode = ref(false);
const restoredActivityId = ref(''); // 恢復的活動 ID

// HiTeach 名單相關
const classRosterSource = ref(''); // 名單來源
const classRoster = ref([]); // 解析後的名單陣列
const useHiTeachRoster = ref(false); // 是否使用 HiTeach 名單

// 左側玩家資訊（雙人模式）
const leftStudentInfo = reactive({
  name: '',
  seatNumber: ''
});
const leftStudentInfoErrors = reactive({
  name: '',
  seatNumber: ''
});

// 右側玩家資訊（單人和雙人模式都使用）
const rightStudentInfo = reactive({
  name: '',
  seatNumber: ''
});
const rightStudentInfoErrors = reactive({
  name: '',
  seatNumber: ''
});

let gameConfig = reactive({
  topic: t('gameActManager.emptyState'),
  correctVocabularyList: [],
  errorVocabularyList: [],
  gameDuration: 0,
  background: '',
  gameSpeed: 2
});

// 初始化 Auth Token
const initializeAuth = async () => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_token_expiry");
    try {
        const res = await api.main.getTokenUnlogin();
        const exp = jwt_decode(res.auth_token).exp;
        localStorage.setItem("auth_token", res.auth_token);
        localStorage.setItem("auth_token_expiry", exp);
    } catch (error) {
        console.error("Error fetching auth token", error);
    }
};

// 從 blob URL 載入配置
const loadConfigFromUrl = async (url) => {
    try {
        const response = await fetch(url);
        
        if (!response.ok) {
            throw new Error('載入失敗');
        }
        
        const config = await response.json();
        Object.assign(gameConfig, JSON.parse(config.data.gameConfig));
        timeRemaining.value = gameConfig.gameDuration;
        
        iviewMessage.success({
            content: t('PvPgame.configLoadSuccess'),
            duration: 2,
            closable: true
        });
    } catch (error) {
        iviewMessage.error({
            content: t('PvPgame.configLoadFailed'),
            duration: 3,
            closable: true
        });
        
        // 使用預設配置
        Object.assign(gameConfig, {
            topic: "Choose Fruits (Demo)",
            correctVocabularyList: ["apple", "banana", "orange", "grape", "watermelon", "strawberry", "blueberry", "pineapple", "kiwi", "mango"],
            errorVocabularyList: ["pen", "pencil", "notebook", "note", "notebook", "notebook", "notebook", "notebook", "notebook", "notebook"],
            gameDuration: 40,
            background: "desert",
            gameSpeed: 2
        });
        timeRemaining.value = gameConfig.gameDuration;
    }
};

onMounted(async () => {
    // 載入 HiTeach 版本號
    loadVersion();

    // ========================================
    // 優先級 0：檢查是否從其他遊戲切換過來
    // ========================================
    const urlParams = new URLSearchParams(window.location.search);
    const switched = urlParams.get('switched');

    // ========================================
    // 優先級 1：正式活動模式 (actId)
    // ========================================
    const actId = urlParams.get('actId');

    // 依據 actId 綁定的切換狀態判斷是否為「此活動」的模板切換
    let hasSwitchedThisActivity = false;
    try {
        const stored = JSON.parse(sessionStorage.getItem('hasGameSwitched') || '{}');
        if (stored && stored.actId && stored.actId === actId && stored.switched) {
            hasSwitchedThisActivity = true;
        }
    } catch (e) {
        hasSwitchedThisActivity = false;
    }

    if (switched === '1' || hasSwitchedThisActivity) {
        hasGameSwitched.value = true;
        // 強制設定為單人模式（從打地鼠切換過來）
        playerCount.value = 1;
        console.log('🔄 檢測到從打地鼠切換，設定為單人模式，將不會提交成績');
    }

    // ========================================
    // 優先級 0.5：恢復模式（教師端 iframe 翻頁恢復）
    // 由 HTCC 在翻頁回本頁時，於 URL 加上 ?restored=true&activityId={id}
    // HT 桌面版自行管理翻頁，不經過此機制
    // ========================================
    const restored = urlParams.get('restored');
    const restoredActId = urlParams.get('activityId');

    if (restored === 'true' && restoredActId) {
        console.log('[HTpvpGamePlay] [恢復模式] activityId:', restoredActId);

        // 設定恢復模式狀態
        isRestoredMode.value = true;
        restoredActivityId.value = restoredActId;

        // 設定 pushedActivityId，讓「查看成績」按鈕出現
        pushedActivityId.value = restoredActId;
        pushedActivityName.value = '已恢復的遊戲活動';

        // 嘗試從 API 載入活動資訊（用於顯示遊戲名稱等）
        Loading.show({ message: t('Game.gameLoading') });
        try {
            // 先初始化 auth_token
            await initializeAuth();

            const response = await gameActivityAPI.getGameActivity({ id: restoredActId });
            const activity = response.data;

            if (activity) {
                pushedActivityName.value = activity.activityName || '摘星星遊戲';

                // 解析 gameContentSettings 來載入遊戲配置（用於顯示遊戲資訊）
                if (activity.gameContentSettings) {
                    try {
                        const settings = JSON.parse(activity.gameContentSettings);
                        Object.assign(gameConfig, settings);
                        gameConfig.topic = activity.activityName || settings.topic || '摘星星遊戲';

                        console.log('[HTpvpGamePlay] [恢復模式] 已載入遊戲配置:', gameConfig);
                    } catch (e) {
                        console.warn('[HTpvpGamePlay] [恢復模式] 解析 gameContentSettings 失敗:', e);
                    }
                }

                // 設定 classRosterSource（用於成績查詢）
                classRosterSource.value = activity.classRosterSource || 'general';
                // 恢復短網址顯示
                if (activity.shortUrlCode) {
                    const host = locale.value?.toLowerCase() === 'zh-cn' ? 'hiteach.cn' : 'hiteach.cc';
                    pushedShortUrl.value = `https://${host}/${activity.shortUrlCode}`;
                }
            }
        } catch (error) {
            console.warn('[HTpvpGamePlay] [恢復模式] 載入活動資訊失敗:', error);
            // 即使載入失敗，仍保持恢復模式，使用預設的遊戲配置
            gameConfig.topic = '摘星星遊戲';
        } finally {
            Loading.hide();
        }

        // 恢復模式下使用 Demo 模式的 UI（顯示推送和查看成績按鈕）
        // 不執行後續的 actId 模式邏輯
        console.log('[HTpvpGamePlay] [恢復模式] 已啟用恢復模式 UI');
        return;
    }
    // ========== 恢復模式檢查結束 ==========

    if (actId) {
        isFromActId.value = true;
        Loading.show({ message: t('Game.gameLoading') });

        // 先初始化 auth_token（actId 模式需要匿名 token）
        await initializeAuth();

        try {
            // 呼叫 API 獲取活動資料
            const response = await gameActivityAPI.getGameActivity({ id: actId });
            console.log('🎮 API Response:', response);
            const activity = response.data;
            console.log('🎯 Activity Data:', activity);

            // 🆕 檢查 currentGame 是否與當前頁面匹配（使用中央化路由驗證）
            if (validateAndRedirectGame(activity, ['star', 'pvp'], router, actId, switched)) {
                return; // 已重導向，中斷後續邏輯
            }

            // 讀取是否允許切換遊戲模板
            allowTemplateChange.value = activity.allowTemplateChange ?? true; // 預設允許
            console.log('🔀 Allow Template Change:', allowTemplateChange.value);

            // 檢查是否為原始遊戲（用於判斷是否提交成績）
            const originalGame = activity.currentGame; // API 回傳的原始遊戲類型：'star' 或 'mole'
            const isOriginalGame = originalGame === 'star'; // 摘星星頁面：檢查原始遊戲是否為 star
            console.log('🎮 Original Game:', originalGame, '| Is Original Game:', isOriginalGame);

            // 如果當前是原始遊戲，即使 URL 有 switched=1，也要提交成績
            if (isOriginalGame && hasGameSwitched.value) {
                console.log('✅ 檢測到返回原始遊戲（摘星星），將恢復成績提交功能');
                hasGameSwitched.value = false; // 重置切換標記
                sessionStorage.removeItem('hasGameSwitched'); // 清除 session 標記（包括 actId 綁定 JSON）
            }

            // 檢查活動是否開放進入
            if (!activity.enableEntry) {
                iviewMessage.warning({
                    content: t('gameActManager.activityNotOpen'),
                    duration: 0,
                    closable: true
                });
                beforeGame.value = false;
                return;
            }

            // 解析遊戲設定
            console.log('📝 Raw gameContentSettings:', activity.gameContentSettings);
            const settings = JSON.parse(activity.gameContentSettings);
            console.log('✅ Parsed Settings:', settings);

            // 🆕 解析 HiTeach 名單資料
            classRosterSource.value = activity.classRosterSource || '';
            useHiTeachRoster.value = settings.useHiTeachRoster || false;

            if (classRosterSource.value === 'HT' && activity.classRoster) {
                try {
                    classRoster.value = JSON.parse(activity.classRoster);
                    console.log('👥 HiTeach 名單:', classRoster.value);
                } catch (error) {
                    console.error('❌ 解析 classRoster 失敗:', error);
                    classRoster.value = [];
                }
            }

            // 檢查 settings 是否為空，若為空則使用預設值
            if (Object.keys(settings).length === 0) {
                console.warn('⚠️ gameContentSettings 是空的，使用預設遊戲參數');
                // 摘星星預設參數（切換過來時強制沙漠風格）
                Object.assign(gameConfig, {
                    topic: activity.activityName || '摘星星遊戲',
                    correctVocabularyList: ['apple', 'banana', 'orange', 'grape', 'watermelon'],
                    errorVocabularyList: ['pen', 'pencil', 'notebook', 'eraser', 'ruler'],
                    gameDuration: 40,
                    background: 'desert',  // 預設就是沙漠，切換過來也是沙漠
                    gameSpeed: 2
                });
                // 設定玩家數量：優先從 activity.playerCount 讀取
                if (!hasGameSwitched.value) {
                    const apiPlayerCount = activity.playerCount;
                    if (apiPlayerCount === 1 || apiPlayerCount === 2) {
                        playerCount.value = apiPlayerCount;
                        console.log('✅ 從 activity.playerCount 設定:', playerCount.value);
                    } else {
                        playerCount.value = 2;
                        console.log('✅ 使用預設值:', playerCount.value);
                    }
                }
            } else {
                // 處理 API 回傳的資料格式差異
                // API 使用 vocabularyList，程式碼使用 correctVocabularyList
                if (settings.vocabularyList && !settings.correctVocabularyList) {
                    settings.correctVocabularyList = settings.vocabularyList;
                }
                Object.assign(gameConfig, settings);

                // 設定玩家數量（如果從其他遊戲切換過來，保持單人模式；否則從 API 讀取）
                if (!hasGameSwitched.value) {
                    // 優先從 activity.playerCount 讀取，其次從 settings.playerCount，最後預設為 2
                    const apiPlayerCount = activity.playerCount;
                    const settingsPlayerCount = settings.playerCount;

                    console.log('🔍 activity.playerCount:', apiPlayerCount);
                    console.log('🔍 settings.playerCount:', settingsPlayerCount);

                    if (apiPlayerCount === 1 || apiPlayerCount === 2) {
                        playerCount.value = apiPlayerCount;
                        console.log('✅ 從 activity.playerCount 設定:', playerCount.value);
                    } else if (settingsPlayerCount === 1 || settingsPlayerCount === 2) {
                        playerCount.value = settingsPlayerCount;
                        console.log('✅ 從 settings.playerCount 設定:', playerCount.value);
                    } else {
                        playerCount.value = 2; // 預設雙人模式
                        console.log('✅ 使用預設值:', playerCount.value);
                    }
                } else {
                    // 從打地鼠切換過來，強制使用沙漠風格
                    gameConfig.background = 'desert';
                }
                // 如果 hasGameSwitched.value = true，則保持之前設定的 playerCount.value = 1
            }

            console.log('🎲 Final gameConfig:', gameConfig);
            console.log('👥 Player Count:', playerCount.value);
            console.log('🔄 Has Switched:', hasGameSwitched.value);
            console.log('🏜️ Background:', gameConfig.background);
            timeRemaining.value = gameConfig.gameDuration;

            // 初始化音效，確保 actId 模式（含單人）有聲音
            initSounds();
            // 摘星星所有模式皆使用固定 BGM
            initStarDemoBGM();

            iviewMessage.success({
                content: t('gameActManager.activityLoadSuccess'),
                duration: 2,
                closable: true
            });

            // 🆕 檢查 webirs_seatid 和 webirs_name 參數，自動帶入學生資訊（單雙人模式都帶入相同資訊）
            const webirsSeatId = urlParams.get('webirs_seatid');
            const webirsName = urlParams.get('webirs_name');

            if (webirsSeatId || webirsName) {
                console.log('📋 檢測到 WebIRS 參數:', { webirsSeatId, webirsName });

                // 使用 nextTick 確保 DOM 已更新
                await nextTick();

                if (classRosterSource.value === 'HT' && classRoster.value.length > 0) {
                    // HiTeach 名單模式：根據座號找到對應的學生並選中
                    const matchedMember = classRoster.value.find(
                        member => String(member.seatID) === webirsSeatId
                    );

                    if (matchedMember) {
                        // 設定左右兩側的學生資訊（單雙人模式都帶入相同資訊）
                        leftStudentInfo.name = matchedMember.memberName;
                        leftStudentInfo.seatNumber = String(matchedMember.seatID);
                        rightStudentInfo.name = matchedMember.memberName;
                        rightStudentInfo.seatNumber = String(matchedMember.seatID);

                        // 設定下拉選單的值（需要等待 DOM 渲染）
                        await nextTick();
                        const leftSelectElement = document.querySelector('.student-info-left .student-select');
                        const rightSelectElement = document.querySelector('.student-info-right .student-select');
                        const selectValue = `${matchedMember.memberName}-${matchedMember.seatID}`;

                        if (leftSelectElement) {
                            leftSelectElement.value = selectValue;
                        }
                        if (rightSelectElement) {
                            rightSelectElement.value = selectValue;
                        }

                        console.log('✅ [HT名單模式] 已自動選中學生（左右兩側）:', matchedMember);
                    } else if (webirsName) {
                        // 如果座號沒有匹配到，但有名字參數，嘗試用名字匹配
                        const matchedByName = classRoster.value.find(
                            member => member.memberName === decodeURIComponent(webirsName)
                        );

                        if (matchedByName) {
                            leftStudentInfo.name = matchedByName.memberName;
                            leftStudentInfo.seatNumber = String(matchedByName.seatID);
                            rightStudentInfo.name = matchedByName.memberName;
                            rightStudentInfo.seatNumber = String(matchedByName.seatID);

                            await nextTick();
                            const leftSelectElement = document.querySelector('.student-info-left .student-select');
                            const rightSelectElement = document.querySelector('.student-info-right .student-select');
                            const selectValue = `${matchedByName.memberName}-${matchedByName.seatID}`;

                            if (leftSelectElement) {
                                leftSelectElement.value = selectValue;
                            }
                            if (rightSelectElement) {
                                rightSelectElement.value = selectValue;
                            }

                            console.log('✅ [HT名單模式] 透過名字匹配到學生（左右兩側）:', matchedByName);
                        } else {
                            console.warn('⚠️ [HT名單模式] 未找到匹配的學生，座號:', webirsSeatId, '名字:', webirsName);
                        }
                    }
                } else {
                    // 手動輸入模式：直接帶入座號和名字（左右兩側都帶入相同資訊）
                    if (webirsSeatId) {
                        leftStudentInfo.seatNumber = webirsSeatId;
                        rightStudentInfo.seatNumber = webirsSeatId;
                    }
                    if (webirsName) {
                        const decodedName = decodeURIComponent(webirsName);
                        leftStudentInfo.name = decodedName;
                        rightStudentInfo.name = decodedName;
                    }
                    console.log('✅ [手動輸入模式] 已自動填入學生資訊（左右兩側）:', {
                        seatNumber: leftStudentInfo.seatNumber,
                        name: leftStudentInfo.name
                    });
                }
            }

            return; // ✅ 跳出，不執行後續邏輯
        } catch (error) {
            console.error('載入活動失敗:', error);
            iviewMessage.error({
                content: t('gameActManager.loadActivityFailed'),
                duration: 3,
                closable: true
            });
            isFromActId.value = false;
            // 繼續執行後續邏輯（使用 Demo 模式）
        } finally {
            Loading.hide();
        }
    }

    // ========================================
    // 優先級 2：預覽模式檢查
    // ========================================
    const previewData = localStorage.getItem('game_preview_data');
    const previewTimestamp = localStorage.getItem('game_preview_timestamp');

    if (previewData && previewTimestamp) {
        const now = Date.now();
        const timestamp = parseInt(previewTimestamp, 10);
        const fiveMinutes = 5 * 60 * 1000;

        // 檢查是否在 5 分鐘內
        if (now - timestamp < fiveMinutes) {
            try {
                const params = JSON.parse(previewData);

                // 檢查遊戲類型是否匹配（摘星星）
                if (params.currentGame === 'star') {
                    loadPreviewMode(params);

                    // 🆕 延遲清除 localStorage（30秒後），避免頁面重新整理時資料遺失
                    setTimeout(() => {
                        localStorage.removeItem('game_preview_data');
                        localStorage.removeItem('game_preview_timestamp');
                        console.log('[摘星星] 預覽資料已清除（30秒後）');
                    }, 30000); // 30秒後清除

                    return; // ✅ 跳出，不執行後續邏輯
                }
            } catch (error) {
                console.error('解析預覽參數失敗:', error);
            }
        }

        // 過期或解析失敗則清除
        localStorage.removeItem('game_preview_data');
        localStorage.removeItem('game_preview_timestamp');
    }

    // ========================================
    // 優先級 2：原本的邏輯（Blob URL 或 WebView）
    // ========================================
    // 檢查是否有 config_url 參數 - 使用原本的方式
    const fullUrl = window.location.href;
    const configUrlIndex = fullUrl.indexOf('config_url=');

    if (configUrlIndex !== -1) {
        // 取得 config_url= 之後的所有內容（直到字串結束）
        configUrl.value = fullUrl.substring(configUrlIndex + 'config_url='.length);
        hasConfigUrl.value = true;
        await loadConfigFromUrl(configUrl.value);
        // 初始化音效（Blob 來源配置完成後）
        initSounds();
        // Demo (config_url) 使用固定摘星星 BGM
        initStarDemoBGM();
    } else {
        hasConfigUrl.value = false;

        // Demo/WebView 模式：設定預設玩家數量為雙人模式
        if (playerCount.value === null) {
            playerCount.value = 2;
            console.log('✅ Demo/WebView 模式：設定 playerCount = 2');
        }

        // 🔧 改善：只有在 iframe 環境中才優先從 sessionStorage 讀取配置
        let configLoaded = false;

        if (isInIframe.value) {
            // iframe 模式：優先從 sessionStorage 讀取配置
            console.log('[iframe 模式] 優先從 sessionStorage 讀取配置');
            const savedConfig = sessionStorage.getItem('PvPGameConfig');

            if (savedConfig) {
                try {
                    const config = JSON.parse(savedConfig);
                    console.log('✅ [iframe] 從 sessionStorage 載入遊戲配置:', config);
                    Object.assign(gameConfig, config);
                    timeRemaining.value = gameConfig.gameDuration;
                    configLoaded = true;

                    // 發送訊息通知 iframe 配置已載入（可選）
                    messageBridge.postMessage({
                        action: 'Init.PvPGame',
                        data: config
                    });
                } catch (error) {
                    console.error('❌ [iframe] 解析 sessionStorage 配置失敗:', error);
                    configLoaded = false;
                }
            }
        }

        // 如果不是 iframe 模式，或 sessionStorage 沒有配置，使用原有邏輯
        if (!configLoaded) {
            if (isInIframe.value) {
                console.log('⚠️ [iframe] sessionStorage 無配置，使用 Demo 模式');
            } else {
                console.log('[非 iframe 模式] 使用原有訊息機制');
            }

            const demoConfig = {
                topic: "Choose Fruits (Demo)",
                correctVocabularyList: ["apple", "banana", "orange", "grape", "watermelon", "strawberry", "blueberry", "pineapple", "kiwi", "mango"],
                errorVocabularyList: ["pen", "pencil", "notebook", "note", "notebook", "notebook", "notebook", "notebook", "notebook", "notebook"],
                gameDuration: 40,
                background: "desert",
                gameSpeed: 2
            };

            Object.assign(gameConfig, demoConfig);
            timeRemaining.value = gameConfig.gameDuration;

            // 發送初始化訊息
            messageBridge.postMessage({ action: 'Init.PvPGame', data: demoConfig });
        }

        // 保存監聽器引用以便在 onUnmounted 時移除
        // 仍然監聽訊息，以支援外部更新配置的情況
        const pvpGameMessageHandler = (event) => {
            const msg = event.data;
            console.log('[HTpvpGamePlay] 收到訊息:', msg.action, msg);

            // 監聽 Init.PvPGame 訊息，載入遊戲配置
            // 注意：Init.GameHome 已由 useGameMessageBridge composable 統一處理
            if (msg.action === 'Init.PvPGame') {
                console.log('📥 收到 Init.PvPGame 訊息，更新配置');
                // 若新設定不含 restoredData，嘗試從 sessionStorage 保留（設定未變動時維持已推送狀態）
                if (!msg.data.restoredData) {
                    try {
                        const existingConfig = JSON.parse(sessionStorage.getItem('PvPGameConfig') || '{}');
                        if (existingConfig.restoredData) {
                            const existComp = JSON.stringify({ topic: existingConfig.topic, correctVocabularyList: existingConfig.correctVocabularyList, errorVocabularyList: existingConfig.errorVocabularyList, gameDuration: existingConfig.gameDuration, background: existingConfig.background, gameSpeed: existingConfig.gameSpeed, useHiTeachRoster: existingConfig.useHiTeachRoster });
                            const newComp = JSON.stringify({ topic: msg.data.topic, correctVocabularyList: msg.data.correctVocabularyList, errorVocabularyList: msg.data.errorVocabularyList, gameDuration: msg.data.gameDuration, background: msg.data.background, gameSpeed: msg.data.gameSpeed, useHiTeachRoster: msg.data.useHiTeachRoster });
                            if (existComp === newComp) msg.data = { ...msg.data, restoredData: existingConfig.restoredData };
                        }
                    } catch (e) { /* sessionStorage 讀取失敗，略過保留 */ }
                }
                Object.assign(gameConfig, msg.data);
                timeRemaining.value = gameConfig.gameDuration;
                // 同步 useHiTeachRoster ref（校園版預設勾選）
                if (msg.data.useHiTeachRoster !== undefined) {
                    useHiTeachRoster.value = !!msg.data.useHiTeachRoster;
                } else if (schoolVers.value) {
                    useHiTeachRoster.value = true;
                }
                // 將設定存入 sessionStorage
                sessionStorage.setItem('PvPGameConfig', JSON.stringify(msg.data));
                // 若帶有 restoredData，恢復推送狀態（短網址、查看成績按鈕）
                if (msg.data.restoredData?.activityId) {
                    pushedActivityId.value = msg.data.restoredData.activityId;
                    pushedShortUrl.value = msg.data.restoredData.shortUrl || '';
                    pushedActivityName.value = gameConfig.topic || '摘星星遊戲';
                    console.log('[HTpvpGamePlay] 恢復推送狀態 activityId:', msg.data.restoredData.activityId);
                }
                // WebView 傳入配置後再初始化音效，避免未就緒
                initSounds();
                // WebView Demo 使用固定摘星星 BGM
                initStarDemoBGM();
            }
        };
        messageBridge.addEventListener(pvpGameMessageHandler);

        // 儲存監聽器引用到外部變數
        messageHandlerRef = pvpGameMessageHandler;

        // Demo/Web 路徑先行初始化音效，以便按下播放鍵即可發聲
        initSounds();
        // Demo/Web 使用固定摘星星 BGM
        initStarDemoBGM();
    }

    // 加入視窗大小調整監聽器
    window.addEventListener('resize', handleResize);
});



// 遊戲狀態
const gameStarted = ref(false);
const gamePaused = ref(false);
const gameOver = ref(false);
const timeRemaining = ref(0);
const leftScore = ref(0);
const rightScore = ref(0);
const scoreSubmitted = ref(false); // 防抖旗標：確保成績只提交一次
const leftSubmitResult = ref(null); // 左側玩家排名資訊
const rightSubmitResult = ref(null); // 右側玩家排名資訊
const gameAreaRef = ref(null);

// 遊戲結果統計
const leftCorrectCount = ref(0);
const leftWrongCount = ref(0);
const leftMaxCombo = ref(0);
const rightCorrectCount = ref(0);
const rightWrongCount = ref(0);
const rightMaxCombo = ref(0);
const leftCurrentCombo = ref(0);
const rightCurrentCombo = ref(0);

// Ready, Go! 相關狀態

// 答案彈窗狀態
const showAnswerModal = ref(false);

// 答案彈窗切換，與 gameOver 互為相反
function toggleAnswerModal() {
showAnswerModal.value = !showAnswerModal.value;
}

const showReadyGo = ref(false);
const readyGoText = ref('');
const isCountingDown = ref(false);

const leftBackground = ref('');
const rightBackground = ref('');

function setRandomBackgrounds() {
  if (gameConfig.background === 'desert') {
    leftBackground.value = `url("${require('@/assets/HTwbActAssets/PvPgameObj/green_star.webp')}")`;
    rightBackground.value = `url("${require('@/assets/HTwbActAssets/PvPgameObj/orange_star.webp')}")`;
  } else if (gameConfig.background === 'ocean') {
    leftBackground.value = `url("${require('@/assets/HTwbActAssets/PvPgameObj/dark_blue_fish.webp')}")`;
    rightBackground.value = `url("${require('@/assets/HTwbActAssets/PvPgameObj/dark_green_fish.webp')}")`;
  } else if (gameConfig.background === 'forest') {
    const leftItems = ['orange', 'red_apple', 'tomato'];
    const rightItems = ['orange', 'red_apple', 'green_lime', 'tomato'];
    let leftRandom = leftItems[Math.floor(Math.random() * leftItems.length)];
    let rightRandom = rightItems[Math.floor(Math.random() * rightItems.length)];
    // 保證左右不一樣
    let tries = 0;
    while (rightRandom === leftRandom && tries < 10) {
      rightRandom = rightItems[Math.floor(Math.random() * rightItems.length)];
      tries++;
    }
    leftBackground.value = `url("${require('@/assets/HTwbActAssets/PvPgameObj/' + leftRandom + '.webp')}")`;
    rightBackground.value = `url("${require('@/assets/HTwbActAssets/PvPgameObj/' + rightRandom + '.webp')}")`;
  } else {
    leftBackground.value = `url("${require('@/assets/HTwbActAssets/PvPgameObj/green_star.webp')}")`;
    rightBackground.value = `url("${require('@/assets/HTwbActAssets/PvPgameObj/orange_star.webp')}")`;
  }
}


const mockImg = computed(() => {
  const backgrounds = {
    forest: 'fantasy_forest',
    ocean: 'ocean_world',
    desert: 'desert_oasis',
  };
  return gameConfig.background ? `url("${require('@/assets/HTwbActAssets/' + backgrounds[gameConfig.background] + '.webp')}")` : ''
})

// 掉落物品
const leftItems = ref([]);
const rightItems = ref([]);

// 動畫分數效果
const flyingScoreEffects = ref([]);
let flyingScoreId = 0;

// 游戏循环和计时器
let gameLoop = null;
let timerInterval = null;

// 游戏区域尺寸
let gameAreaWidth = 0;
let gameAreaHeight = 0;

// 遊戲開始前彈窗
const beforeGame = ref(true);

// 預分配的掉落物品佇列
const itemQueues = reactive({
  leftQueue: [],
  rightQueue: []
});

// 遊戲測試統計
const gameStats = reactive({
  totalGenerated: 0,
  totalClicked: 0,
  totalMissed: 0,
  leftQueueStartSize: 0,
  rightQueueStartSize: 0,
  gameStartTime: 0,
  lastReportTime: 0
});

// 記錄左右兩側已生成的正確項和錯誤項數量
const itemCounter = reactive({
  leftCorrect: 0,
  leftError: 0,
  rightCorrect: 0,
  rightError: 0
});

// 預先分配整場遊戲的掉落物品，確保公平性
const preAllocateGameItems = () => {
  // 清空佇列
  itemQueues.leftQueue = [];
  itemQueues.rightQueue = [];
  
  // 根據遊戲時長計算合理的物品總數（保守估計，確保能全部掉完）
  let itemsPerSecond;
  if (gameConfig.gameDuration <= 20) {
    itemsPerSecond = 0.5; // 20秒: 10個/側
  } else if (gameConfig.gameDuration <= 30) {
    itemsPerSecond = 0.5; // 30秒: 15個/側  
  } else {
    itemsPerSecond = 0.45; // 40秒: 18個/側
  }
  const totalItemsPerSide = Math.floor(gameConfig.gameDuration * itemsPerSecond);
  
  // 確保正確和錯誤物品比例（60%正確，40%錯誤）
  const correctItemsPerSide = Math.floor(totalItemsPerSide * 0.6);
  const errorItemsPerSide = totalItemsPerSide - correctItemsPerSide;
  
  // 為左右兩側分配相同數量的正確和錯誤項目
  for (let side of ['left', 'right']) {
    const queue = [];
    
    // 添加正確項目
    for (let i = 0; i < correctItemsPerSide; i++) {
      const randomCorrectItem = gameConfig.correctVocabularyList[
        Math.floor(Math.random() * gameConfig.correctVocabularyList.length)
      ];
      queue.push({
        text: randomCorrectItem,
        isCorrect: true
      });
    }
    
    // 添加錯誤項目
    for (let i = 0; i < errorItemsPerSide; i++) {
      const randomErrorItem = gameConfig.errorVocabularyList[
        Math.floor(Math.random() * gameConfig.errorVocabularyList.length)
      ];
      queue.push({
        text: randomErrorItem,
        isCorrect: false
      });
    }
    
    // 打亂順序
    for (let i = queue.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [queue[i], queue[j]] = [queue[j], queue[i]];
    }
    
    // 分配到對應佇列
    if (side === 'left') {
      itemQueues.leftQueue = queue;
    } else {
      itemQueues.rightQueue = queue;
    }
  }
  
  // 記錄統計資料
  gameStats.leftQueueStartSize = totalItemsPerSide;
  gameStats.rightQueueStartSize = totalItemsPerSide;
  gameStats.totalGenerated = 0;
  gameStats.totalClicked = 0;
  gameStats.totalMissed = 0;
  
  console.log(`📊 預分配完成 - 遊戲時長: ${gameConfig.gameDuration}秒`);
  console.log(`📦 左側: ${totalItemsPerSide}項 (${correctItemsPerSide}正確 + ${errorItemsPerSide}錯誤)`);
  console.log(`📦 右側: ${totalItemsPerSide}項 (${correctItemsPerSide}正確 + ${errorItemsPerSide}錯誤)`);
  console.log(`⏱️ 預期每秒生成: ${(totalItemsPerSide / gameConfig.gameDuration).toFixed(1)}個物品`);
  console.log(`🎯 理論最高分: 左側${correctItemsPerSide * 10}分 | 右側${correctItemsPerSide * 10}分`);
  
  // 顯示詳細分配內容
  console.log(`\n📋 詳細分配清單:`);
  console.log(`✅ 左側正確項目: [${itemQueues.leftQueue.filter(item => item.isCorrect).map(item => item.text).join(', ')}]`);
  console.log(`❌ 左側錯誤項目: [${itemQueues.leftQueue.filter(item => !item.isCorrect).map(item => item.text).join(', ')}]`);
  console.log(`✅ 右側正確項目: [${itemQueues.rightQueue.filter(item => item.isCorrect).map(item => item.text).join(', ')}]`);
  console.log(`❌ 右側錯誤項目: [${itemQueues.rightQueue.filter(item => !item.isCorrect).map(item => item.text).join(', ')}]`);
};

// 生成随機位置，確保不會與現有物體重疊
const getRandomPosition = () => {
  // 單人模式使用全寬，雙人模式使用半寬
  const width = isSinglePlayerMode.value
    ? gameAreaWidth - 240  // 單人模式：全寬 - 邊界
    : gameAreaWidth / 2 - 240; // 雙人模式：半寬 - 邊界
  const itemSize = 150; // 物體寬度
  const minDistance = itemSize + 10; // 物體之間的最小距離

  // 嘗試多次生成不重疊的位置
  for (let attempts = 0; attempts < 20; attempts++) {
    const x = 100 + Math.floor(Math.random() * width);
    const y =-40- Math.random() * 500; // 從頂部開始掉落

    // 檢查是否與左側現有物體重疊
    const leftOverlap = leftItems.value.some(item => {
      return Math.abs(item.x - x) < minDistance && Math.abs(item.y - y) < minDistance;
    });

    // 檢查是否與右側現有物體重疊
    const rightOverlap = rightItems.value.some(item => {
      return Math.abs(item.x - x) < minDistance && Math.abs(item.y - y) < minDistance;
    });

    // 如果沒有重疊，返回這個位置
    if (!leftOverlap && !rightOverlap) {
      return { x, y };
    }
  }

  // 如果多次嘗試後仍無法找到不重疊的位置，則延遲生成
  return null;
};

// 生成新的掉落物品对（從預分配佇列中取出）
const createFallingItemPair = (position) => {
  // 檢查佇列是否還有物品
  if (itemQueues.leftQueue.length === 0 || itemQueues.rightQueue.length === 0) {
    return null; // 沒有更多物品可生成
  }
  
  // 使用傳入的位置，或者生成新的位置
  const leftPosition = position || getRandomPosition();
  if (!leftPosition) return null;

  const rightPosition = position || getRandomPosition();
  if (!rightPosition) return null;
  
  const speed = gameConfig.gameSpeed * 2; // 加大速度倍數，讓速度差異更明顯

  // 從預分配佇列中取出物品（先進先出）
  const leftItem = itemQueues.leftQueue.shift(); // 取出第一個物品
  const rightItem = itemQueues.rightQueue.shift(); // 取出第一個物品

  // 建立兩個物品物件
  const commonId = Date.now() + Math.random();

  const leftFallingItem = {
    id: commonId + '-left',
    text: leftItem.text,
    isCorrect: leftItem.isCorrect,
    x: leftPosition.x,
    y: leftPosition.y,
    speed: speed,
    side: 'left'
  };

  const rightFallingItem = {
    id: commonId + '-right',
    text: rightItem.text,
    isCorrect: rightItem.isCorrect,
    x: rightPosition.x,
    y: rightPosition.y,
    speed: speed,
    side: 'right'
  };

  return { leftFallingItem, rightFallingItem };
};

// 更新掉落物品位置
const updateFallingItems = () => {
  // 更新左侧
  const leftBefore = leftItems.value.length;
  leftItems.value = leftItems.value.filter(item => {
    item.y += item.speed;
    return item.y < gameAreaHeight; // 移除已经掉出屏幕的物品
  });
  const leftMissed = leftBefore - leftItems.value.length;

  // 更新右侧
  const rightBefore = rightItems.value.length;
  rightItems.value = rightItems.value.filter(item => {
    item.y += item.speed;
    return item.y < gameAreaHeight; // 移除已经掉出屏幕的物品
  });
  const rightMissed = rightBefore - rightItems.value.length;
  
  // 統計掉落數量
  gameStats.totalMissed += leftMissed + rightMissed;

  // 同時為兩側添加新物品，使用相同的位置和速度
  // 動態計算生成機率，確保所有預分配物品都能在遊戲時間內掉完
  const remainingTime = timeRemaining.value;
  const remainingItems = Math.max(itemQueues.leftQueue.length, itemQueues.rightQueue.length);
  
  // 如果還有物品要生成且遊戲還在進行
  if (remainingItems > 0 && remainingTime > 0) {
    // 計算每秒需要生成的物品數量來確保全部掉完
    const requiredSpawnRate = remainingItems / remainingTime;
    // 轉換為每50ms的生成機率（遊戲循環頻率），增加更多安全係數
    const spawnProbability = (requiredSpawnRate / 20) * 1.5; // 增加安全係數從1.2到1.5
    
    // 如果剩餘時間不多，增加生成機率確保物品能掉完
    let finalSpawnProbability = spawnProbability;
    if (remainingTime <= 15 && remainingItems > 3) {
      finalSpawnProbability = Math.min(0.9, spawnProbability * 2.5); // 最後15秒更積極加速生成
    } else if (remainingTime <= 5 && remainingItems > 0) {
      finalSpawnProbability = Math.min(1.0, spawnProbability * 4); // 最後5秒全力衝刺
    }
    
    if (Math.random() < finalSpawnProbability && leftItems.value.length < 6 && rightItems.value.length < 6) {
      const position = getRandomPosition();
      if (position) {
        const itemPair = createFallingItemPair(position);
        if (itemPair) {
          leftItems.value.push(itemPair.leftFallingItem);
          rightItems.value.push(itemPair.rightFallingItem);
          gameStats.totalGenerated += 2; // 左右各一個
        }
      }
    }
  }
  
  // 每5秒報告一次進度
  const currentTime = Date.now();
  if (currentTime - gameStats.lastReportTime > 5000) {
    gameStats.lastReportTime = currentTime;
    const remainingTotal = itemQueues.leftQueue.length + itemQueues.rightQueue.length;
    const processedTotal = gameStats.totalClicked + gameStats.totalMissed;
    console.log(`🕐 剩餘時間: ${remainingTime}秒 | 佇列剩餘: ${remainingTotal}項 | 已處理: ${processedTotal}項 (點擊${gameStats.totalClicked} + 掉落${gameStats.totalMissed})`);
  }
};

// 觸控處理標記，避免重複觸發
const processedTouches = ref(new Set());

// PvP遊戲觸控開始處理 - 立即觸發，支援多點觸控
const handleTouchStart = (event, item, side) => {
  if (!gameStarted.value || gameOver.value) return;
  
  // 遍歷所有新的觸控點
  for (let i = 0; i < event.changedTouches.length; i++) {
    const touch = event.changedTouches[i];
    const touchId = touch.identifier;
    
    // 防止重複處理同一個觸控點
    if (!processedTouches.value.has(touchId)) {
      processedTouches.value.add(touchId);
      
      // 立即觸發物品點擊，不等 touchend
      handleItemClick(item, side);
      
      // 延遲清除觸控標記
      setTimeout(() => {
        processedTouches.value.delete(touchId);
      }, 100);
    }
  }
  
  // 阻止預設行為，避免觸發 click 事件
  event.preventDefault();
  event.stopPropagation();
};

// 觸控結束處理 - 清理狀態
const handleTouchEnd = (event) => {
  // 清理結束的觸控點標記
  for (let i = 0; i < event.changedTouches.length; i++) {
    const touch = event.changedTouches[i];
    processedTouches.value.delete(touch.identifier);
  }
};

const handleItemClick = async (item, side) => {
  if (!gameStarted.value || gameOver.value) return;

  // 取得遊戲區域和物體位置
  const gameArea = gameAreaRef.value;
  const gameRect = gameArea.getBoundingClientRect();
  
  // 使用物體的座標而非點擊位置
  // 將物體的相對座標轉換為絕對座標
  let itemX, itemY;
  
  // 根據側邊選擇正確的物體座標
  if (side === 'left') {
    // 左側物體
    itemX = item.x + gameRect.left;
    itemY = item.y + gameRect.top;
  } else {
    // 右側物體，需要調整座標
    itemX = item.x + gameRect.left + gameRect.width / 2; // 加上遊戲區域寬度的一半
    itemY = item.y + gameRect.top;
  }
  
  // 確保點擊的一側與項目的一側匹配
  const clickSide = side;
  if (clickSide !== side) return;

  // 決定分數字樣
  let effectText = '';
  let isCorrect = false;
  
  if (item.isCorrect) {
    effectText = '+10';
    isCorrect = true;
    // 播放正確音效
    if (correctSound.value) {
      correctSound.value.currentTime = 0; // 重置播放進度
      correctSound.value.play();
    }
  } else {
    effectText = '-1';
    isCorrect = false;
    // 播放錯誤音效
    if (wrongSound.value) {
      wrongSound.value.currentTime = 0; // 重置播放進度
      wrongSound.value.play();
    }
  }

  // 使用物體的位置作為飛行終點座標
  // 將分數效果顯示在物體上
  const targetPos = {
    x: itemX, // 使用物體的 X 座標
    y: itemY  // 使用物體的 Y 座標
  };

  // 新增動畫效果
  const id = flyingScoreId++;
  const effect = {
    id,
    text: effectText,
    isCorrect,
    currentX: itemX,
    currentY: itemY,
    targetX: targetPos.x,
    targetY: targetPos.y,
    opacity: 1
  };
  flyingScoreEffects.value.push(effect);

  // 動畫過程（簡單線性移動，500ms）
  const duration = 500;
  const frameRate = 1000 / 60;
  let elapsed = 0;
  function animate() {
    elapsed += frameRate;
    const t = Math.min(elapsed / duration, 1);
    effect.currentX = itemX + (targetPos.x - itemX) * t;
    effect.currentY = itemY + (targetPos.y - itemY) * t;
    effect.opacity = 1 - t * 0.5;
    if (t < 1) {
      requestAnimationFrame(animate);
    } else {
      // 動畫結束移除
      flyingScoreEffects.value = flyingScoreEffects.value.filter(e => e.id !== id);
    }
  }
  animate();

  // 移除被點擊的物品 & 分數統計
  gameStats.totalClicked++; // 記錄點擊統計
  
  if (side === 'left') {
    leftItems.value = leftItems.value.filter(i => i.id !== item.id);
    if (item.isCorrect) {
      leftScore.value += 10;
      leftCorrectCount.value += 1;
      leftCurrentCombo.value += 1;
      if (leftCurrentCombo.value > leftMaxCombo.value) {
        leftMaxCombo.value = leftCurrentCombo.value;
      }
    } else {
      leftScore.value -= 1; // 答錯扣1分
      leftWrongCount.value += 1;
      leftCurrentCombo.value = 0;
    }
  } else {
    rightItems.value = rightItems.value.filter(i => i.id !== item.id);
    if (item.isCorrect) {
      rightScore.value += 10;
      rightCorrectCount.value += 1;
      rightCurrentCombo.value += 1;
      if (rightCurrentCombo.value > rightMaxCombo.value) {
        rightMaxCombo.value = rightCurrentCombo.value;
      }
    } else {
      rightScore.value -= 1; // 答錯扣1分
      rightWrongCount.value += 1;
      rightCurrentCombo.value = 0;
    }
  }
};
// 格式化时间显示
const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// 切換遊戲狀態（開始/暫停/繼續）
const toggleGame = () => {
  if (!gameStarted.value) {
    if (!isCountingDown.value) {
      showReadyGoCountdown();
    }
  } else if (gamePaused.value) {
    resumeGame();
  } else {
    pauseGame();
  }
};

// 顯示 Ready, Go! 倒數效果
const showReadyGoCountdown = () => {
  // 正式活動模式：驗證學生資訊
  if (isFromActId.value) {
    // 雙人模式：驗證左右兩側玩家資訊
    if (!isSinglePlayerMode.value) {
      // 清除錯誤訊息
      leftStudentInfoErrors.name = '';
      leftStudentInfoErrors.seatNumber = '';
      rightStudentInfoErrors.name = '';
      rightStudentInfoErrors.seatNumber = '';

      // 驗證左側玩家
      if (!leftStudentInfo.name.trim()) {
        leftStudentInfoErrors.name = t('gameActManager.studentNameRequired');
        return;
      }
      if (leftStudentInfo.name.length > 20) {
        leftStudentInfoErrors.name = t('gameActManager.studentNameTooLong');
        return;
      }
      if (leftStudentInfo.seatNumber && !/^\d{1,3}$/.test(leftStudentInfo.seatNumber)) {
        leftStudentInfoErrors.seatNumber = t('gameActManager.seatNumberInvalid');
        return;
      }

      // 驗證右側玩家
      if (!rightStudentInfo.name.trim()) {
        rightStudentInfoErrors.name = t('gameActManager.studentNameRequired');
        return;
      }
      if (rightStudentInfo.name.length > 20) {
        rightStudentInfoErrors.name = t('gameActManager.studentNameTooLong');
        return;
      }
      if (rightStudentInfo.seatNumber && !/^\d{1,3}$/.test(rightStudentInfo.seatNumber)) {
        rightStudentInfoErrors.seatNumber = t('gameActManager.seatNumberInvalid');
        return;
      }

      // 🆕 雙人模式且使用 HiTeach 名單時：檢查兩方座號是否相同
      if (classRosterSource.value === 'HT' && classRoster.value.length > 0) {
        if (leftStudentInfo.seatNumber.trim() === rightStudentInfo.seatNumber.trim()) {
          rightStudentInfoErrors.name = t('gameActManager.seatNumberAlreadyUsed');
          return;
        }
      }

      // 儲存到 sessionStorage (雙人模式)
      sessionStorage.setItem('leftStudentInfo', JSON.stringify({
        name: leftStudentInfo.name.trim(),
        seatNumber: leftStudentInfo.seatNumber.trim()
      }));
      sessionStorage.setItem('rightStudentInfo', JSON.stringify({
        name: rightStudentInfo.name.trim(),
        seatNumber: rightStudentInfo.seatNumber.trim()
      }));
    } else {
      // 單人模式：只驗證右側玩家資訊
      rightStudentInfoErrors.name = '';
      rightStudentInfoErrors.seatNumber = '';

      if (!rightStudentInfo.name.trim()) {
        rightStudentInfoErrors.name = t('gameActManager.studentNameRequired');
        return;
      }
      if (rightStudentInfo.name.length > 20) {
        rightStudentInfoErrors.name = t('gameActManager.studentNameTooLong');
        return;
      }
      if (rightStudentInfo.seatNumber && !/^\d{1,3}$/.test(rightStudentInfo.seatNumber)) {
        rightStudentInfoErrors.seatNumber = t('gameActManager.seatNumberInvalid');
        return;
      }

      // 儲存到 sessionStorage (單人模式)
      sessionStorage.setItem('studentInfo', JSON.stringify({
        name: rightStudentInfo.name.trim(),
        seatNumber: rightStudentInfo.seatNumber.trim()
      }));
    }
  }

  // 停止並重置所有音效
  if (readySound.value) {
    readySound.value.pause();
    readySound.value.currentTime = 0;
  }
  if (goSound.value) {
    goSound.value.pause();
    goSound.value.currentTime = 0;
  }
  if (backgroundMusic.value) {
    backgroundMusic.value.pause();
    backgroundMusic.value.currentTime = 0;
  }
  if (correctSound.value) {
    correctSound.value.pause();
    correctSound.value.currentTime = 0;
  }
  if (wrongSound.value) {
    wrongSound.value.pause();
    wrongSound.value.currentTime = 0;
  }
  if (tieSound.value) {
    tieSound.value.pause();
    tieSound.value.currentTime = 0;
  }
  if (winSound.value) {
    winSound.value.pause();
    winSound.value.currentTime = 0;
  }


  beforeGame.value = false;
  isCountingDown.value = true;
  showReadyGo.value = true;
  readyGoText.value = 'Ready';
  
  // 播放 Ready 音效
  if (readySound.value) {
    readySound.value.currentTime = 0;
    readySound.value.play();
  }
  
  // 2秒後顯示 Go!
  setTimeout(() => {
    readyGoText.value = 'Go!';
    
    // 播放 Go 音效
    if (goSound.value) {
      goSound.value.currentTime = 0;
      goSound.value.play();
    }
    
    // 再過1秒後開始遊戲
    setTimeout(() => {
      showReadyGo.value = false;
      isCountingDown.value = false;
      startGame();
    }, 1000);
  }, 2000);
};

// 開始遊戲
const startGame = () => {
  // 發送遊戲開始協議給 WebView
  messageBridge.postMessage({
    action: "Game.Start",
    type: "pvp"
  });

  // 設定真正的遊戲開始時間（Ready, Go!結束後）
  gameStats.gameStartTime = Date.now();

  setRandomBackgrounds();

  // 初始化遊戲區域尺寸
  if (gameAreaRef.value) {
    const rect = gameAreaRef.value.getBoundingClientRect();
    gameAreaWidth = rect.width;
    gameAreaHeight = rect.height;
  }

  // 預先分配所有掉落物品，確保公平性
  preAllocateGameItems();
  
  // 統計時間將在遊戲真正開始時設定（在Ready, Go!之後）
  gameStats.lastReportTime = Date.now();

  // 重置遊戲狀態
  gameStarted.value = true;
  gamePaused.value = false;
  gameOver.value = false;
  leftScore.value = 0;
  rightScore.value = 0;
  timeRemaining.value = gameConfig.gameDuration;
  leftItems.value = [];
  rightItems.value = [];
  
  // 重置統計數據
  leftCorrectCount.value = 0;
  leftWrongCount.value = 0;
  leftMaxCombo.value = 0;
  rightCorrectCount.value = 0;
  rightWrongCount.value = 0;
  rightMaxCombo.value = 0;
  leftCurrentCombo.value = 0;
  rightCurrentCombo.value = 0;

  // 播放背景音樂
  if (backgroundMusic.value) {
    backgroundMusic.value.play();
  }

  // 啟動遊戲循環，根據遊戲速度調整更新頻率
  const updateInterval = Math.max(30, 70 - (gameConfig.gameSpeed * 10)); // 速度越快，更新越頻繁
  gameLoop = setInterval(() => {
    updateFallingItems();
  }, updateInterval);

  // 啟動計時器
  timerInterval = setInterval(() => {
    if (timeRemaining.value > 0) {
      timeRemaining.value--;
    } else {
      endGame();
    }
  }, 1000);
};

// 視窗大小調整處理
const handleResize = () => {
  windowWidth.value = window.innerWidth;
};

// 關閉小螢幕警告
const dismissWarning = () => {
  isWarningDismissed.value = true;
};

// 暫停遊戲
const pauseGame = () => {
  if (!gameStarted.value || gameOver.value) return;

  gamePaused.value = true;

  // 暫停背景音樂
  if (backgroundMusic.value) {
    backgroundMusic.value.pause();
  }

  // 清除定時器
  clearInterval(gameLoop);
  clearInterval(timerInterval);
};

// 繼續遊戲
const resumeGame = () => {
  if (!gameStarted.value || gameOver.value || !gamePaused.value) return;

  gamePaused.value = false;

  // 繼續播放背景音樂
  if (backgroundMusic.value) {
    backgroundMusic.value.play();
  }

  // 重新啟動遊戲循環，根據遊戲速度調整更新頻率
  const updateInterval = Math.max(30, 70 - (gameConfig.gameSpeed * 10)); // 速度越快，更新越頻繁
  gameLoop = setInterval(() => {
    updateFallingItems();
  }, updateInterval);

  // 重新啟動計時器
  timerInterval = setInterval(() => {
    if (timeRemaining.value > 0) {
      timeRemaining.value--;
    } else {
      endGame();
    }
  }, 1000);
};

// 结束游戏
const endGame = async () => {
  gameStarted.value = false;
  gameOver.value = true;

  // 生成詳細統計報告
  // const gameEndTime = Date.now();
  // const actualGameDuration = (gameEndTime - gameStats.gameStartTime) / 1000;
  // const remainingInQueue = itemQueues.leftQueue.length + itemQueues.rightQueue.length;
  // const itemsOnScreen = leftItems.value.length + rightItems.value.length;
  // const totalStartItems = gameStats.leftQueueStartSize + gameStats.rightQueueStartSize;
  // const totalProcessed = gameStats.totalClicked + gameStats.totalMissed;
  // const completionRate = ((totalProcessed) / totalStartItems * 100).toFixed(1);

  // console.log(`\n🏁 遊戲結束統計報告`);
  // console.log(`⏱️ 設定時長: ${gameConfig.gameDuration}秒 | 實際時長: ${actualGameDuration.toFixed(1)}秒`);
  // console.log(`📦 預分配物品: ${totalStartItems}項 (左${gameStats.leftQueueStartSize} + 右${gameStats.rightQueueStartSize})`);
  // console.log(`🎯 已生成: ${gameStats.totalGenerated}項`);
  // console.log(`👆 玩家點擊: ${gameStats.totalClicked}項`);
  // console.log(`💧 掉落遺失: ${gameStats.totalMissed}項`);
  // console.log(`📺 螢幕剩餘: ${itemsOnScreen}項`);
  // console.log(`🗃️ 佇列剩餘: ${remainingInQueue}項`);
  // console.log(`📊 完成率: ${completionRate}%`);
  // console.log(`${remainingInQueue > 0 ? '⚠️ 警告：還有物品未掉落！' : '✅ 所有物品已掉落完畢'}`);

  // 停止背景音樂
  if (backgroundMusic.value) {
    backgroundMusic.value.pause();
    backgroundMusic.value.currentTime = 0;
  }

  // 根據遊戲結果播放相應音效
  setTimeout(() => {
    if (leftScore.value === rightScore.value) {
      // 平局
      if (tieSound.value) {
        tieSound.value.currentTime = 0;
        tieSound.value.play();
      }
    } else {
      // 有人獲勝
      if (winSound.value) {
        winSound.value.currentTime = 0;
        winSound.value.play();
      }
    }
  }, 500); // 延遲半秒再播放，讓畫面先切換

  // 清除定时器
  clearInterval(gameLoop);
  clearInterval(timerInterval);

  // 🆕 提交成績（僅限正式活動模式且未切換遊戲，且只提交一次）
  if (isFromActId.value && !hasGameSwitched.value && !scoreSubmitted.value) {
    await submitGameScore();
  } else if (isFromActId.value && hasGameSwitched.value) {
    // 切換模板後不提交成績，顯示提示訊息
    iviewMessage.warning({
      content: t('gameActManager.templateSwitchedNoScore'),
      duration: 5,
      closable: true
    });
    console.log('⚠️ 因切換遊戲模板，不提交成績');
  }
};

// 🆕 提交遊戲成績到伺服器
const submitGameScore = async () => {
  // 防抖檢查：如果已經提交過，直接返回
  if (scoreSubmitted.value) {
    console.log('⚠️ 成績已經提交過，跳過重複提交');
    return;
  }

  // 設置旗標，防止重複提交
  scoreSubmitted.value = true;

  try {
    // 從 URL 取得 actId
    const urlParams = new URLSearchParams(window.location.search);
    const actId = urlParams.get('actId');

    if (!actId) {
      console.error('無法提交成績：缺少 actId');
      scoreSubmitted.value = false; // 如果失敗，重置旗標允許重試
      return;
    }

    // 雙人模式：提交兩筆成績
    if (!isSinglePlayerMode.value) {
      // 從 sessionStorage 取得左右玩家資訊
      const leftStudentData = JSON.parse(sessionStorage.getItem('leftStudentInfo') || '{}');
      const rightStudentData = JSON.parse(sessionStorage.getItem('rightStudentInfo') || '{}');

      // 提交左側玩家成績
      if (leftStudentData.name) {
        const leftScoreData = {
          activityId: actId,
          studentName: leftStudentData.name,
          seatNumber: leftStudentData.seatNumber || '',
          score: leftScore.value,
          correctCount: leftCorrectCount.value,
          wrongCount: leftWrongCount.value
        };

        console.log('📤 提交成績資料 (左側玩家):', leftScoreData);

        const leftResponse = await gameActivityAPI.submitScore(leftScoreData);

        if (leftResponse.status === 200) {
          console.log('✅ 成績提交成功 (左側玩家):', leftResponse.data);
          leftSubmitResult.value = leftResponse.data;
        }
      } else {
        console.error('無法提交成績：缺少左側玩家姓名');
      }

      // 提交右側玩家成績
      if (rightStudentData.name) {
        const rightScoreData = {
          activityId: actId,
          studentName: rightStudentData.name,
          seatNumber: rightStudentData.seatNumber || '',
          score: rightScore.value,
          correctCount: rightCorrectCount.value,
          wrongCount: rightWrongCount.value
        };

        console.log('📤 提交成績資料 (右側玩家):', rightScoreData);

        const rightResponse = await gameActivityAPI.submitScore(rightScoreData);

        if (rightResponse.status === 200) {
          console.log('✅ 成績提交成功 (右側玩家):', rightResponse.data);
          rightSubmitResult.value = rightResponse.data;
        }
      } else {
        console.error('無法提交成績：缺少右側玩家姓名');
      }
    } else {
      // 單人模式：只提交一筆成績
      const studentData = JSON.parse(sessionStorage.getItem('studentInfo') || '{}');

      if (!studentData.name) {
        console.error('無法提交成績：缺少學生姓名');
        return;
      }

      const scoreData = {
        activityId: actId,
        studentName: studentData.name,
        seatNumber: studentData.seatNumber || '',
        score: leftScore.value,
        correctCount: leftCorrectCount.value,
        wrongCount: leftWrongCount.value
      };

      console.log('📤 提交成績資料 (單人):', scoreData);

      const response = await gameActivityAPI.submitScore(scoreData);

      if (response.status === 200) {
        console.log('✅ 成績提交成功:', response.data);
        leftSubmitResult.value = response.data;
      }
    }
  } catch (error) {
    console.error('❌ 提交成績失敗:', error);
  }
};

// 重置游戏
const resetGame = () => {
  setRandomBackgrounds();
  gameOver.value = false;
  leftItems.value = [];
  rightItems.value = [];
  beforeGame.value = true;
  gamePaused.value = false;
  scoreSubmitted.value = false; // 重置成績提交旗標，允許下次遊戲提交
  leftSubmitResult.value = null;
  rightSubmitResult.value = null;

  // 清理觸控狀態
  processedTouches.value.clear();

  // 清空物品佇列
  itemQueues.leftQueue = [];
  itemQueues.rightQueue = [];

  // 重置計數器
  itemCounter.leftCorrect = 0;
  itemCounter.leftError = 0;
  itemCounter.rightCorrect = 0;
  itemCounter.rightError = 0;
};

// 🆕 處理左側玩家 HiTeach 名單選擇
const handleLeftRosterSelection = (event) => {
    const selectedValue = event.target.value;

    if (!selectedValue) {
        // 清空選項
        leftStudentInfo.name = '';
        leftStudentInfo.seatNumber = '';
        // 清除錯誤訊息
        leftStudentInfoErrors.name = '';
        leftStudentInfoErrors.seatNumber = '';
        return;
    }

    // 從名單中找到對應的成員
    const selectedMember = classRoster.value.find(
        member => `${member.memberName}-${member.seatID}` === selectedValue
    );

    if (selectedMember) {
        leftStudentInfo.name = selectedMember.memberName;
        leftStudentInfo.seatNumber = String(selectedMember.seatID);
        // 清除錯誤訊息
        leftStudentInfoErrors.name = '';
        leftStudentInfoErrors.seatNumber = '';
        console.log('👤 左側玩家選擇學生:', selectedMember);
    }
};

// 🆕 處理右側玩家 HiTeach 名單選擇
const handleRightRosterSelection = (event) => {
    const selectedValue = event.target.value;

    if (!selectedValue) {
        // 清空選項
        rightStudentInfo.name = '';
        rightStudentInfo.seatNumber = '';
        // 清除錯誤訊息
        rightStudentInfoErrors.name = '';
        rightStudentInfoErrors.seatNumber = '';
        return;
    }

    // 從名單中找到對應的成員
    const selectedMember = classRoster.value.find(
        member => `${member.memberName}-${member.seatID}` === selectedValue
    );

    if (selectedMember) {
        rightStudentInfo.name = selectedMember.memberName;
        rightStudentInfo.seatNumber = String(selectedMember.seatID);
        // 清除錯誤訊息
        rightStudentInfoErrors.name = '';
        rightStudentInfoErrors.seatNumber = '';
        console.log('👤 右側玩家選擇學生:', selectedMember);
    }
};

// 前往設置頁面
const goToSettings = () => {
  const config = {
    topic: gameConfig.topic,
    correctVocabularyList: [...(gameConfig.correctVocabularyList || [])],
    errorVocabularyList: [...(gameConfig.errorVocabularyList || [])],
    gameDuration: gameConfig.gameDuration,
    background: gameConfig.background,
    gameSpeed: gameConfig.gameSpeed
  };
  if (pushedActivityId.value) {
    config.useHiTeachRoster = useHiTeachRoster.value;
    config.restoredData = { activityId: pushedActivityId.value, shortUrl: pushedShortUrl.value };
  }
  sessionStorage.setItem('PvPGameConfig', JSON.stringify(config));
  router.push('/ht-pvpgame-setting');
};

// 切換遊戲模板（打地鼠 ⇄ 摘星星）
// 根據選擇的遊戲模板進行跳轉
const navigateToSelectedGame = () => {
  // 如果選擇的是當前遊戲，不進行跳轉
  if (selectedGameTemplate.value === 'star') {
    return;
  }

  // 在 sessionStorage 標記已切換遊戲（跨頁面保存）
  sessionStorage.setItem('hasGameSwitched', 'true');

  // 保存當前遊戲資訊到 sessionStorage
  sessionStorage.setItem('switchedFromGame', 'star');

  // 取得 URL 參數
  const currentUrl = new URL(window.location.href);
  const actId = currentUrl.searchParams.get('actId');
  const webirsSeatId = currentUrl.searchParams.get('webirs_seatid');
  const webirsName = currentUrl.searchParams.get('webirs_name');
  const webirsQuery = [
    webirsSeatId ? `webirs_seatid=${webirsSeatId}` : '',
    webirsName ? `webirs_name=${webirsName}` : ''
  ].filter(Boolean).join('&');

  // 根據選擇的遊戲模板決定跳轉路徑
  let targetRoute = '';

  switch (selectedGameTemplate.value) {
    case 'mole':
      targetRoute = '/ht-molegame-play';
      break;
    case 'snake':
      targetRoute = '/ht-snakegame-play';
      break;
    default:
      console.error('未知的遊戲模板:', selectedGameTemplate.value);
      return;
  }

  // 構建完整的跳轉路徑
  if (actId) {
    router.push(`${targetRoute}?actId=${actId}&switched=1${webirsQuery ? '&' + webirsQuery : ''}`);
  } else {
    router.push(`${targetRoute}?switched=1${webirsQuery ? '&' + webirsQuery : ''}`);
  }
};

// 已移至 useGameActivityPush composable：
// - createGameActivityFromCurrentConfig
// - generateShortUrl
// - updateActivityWithShortUrl
// - createAndPushShortUrl

// 推送到終端（舊版本 HiTeach）
const sendToWebIRS = () => {
    messageBridge.postMessage({action: 'SendGame.WebIRS', data: {game: 'pvpGame', gameConfig: JSON.stringify(gameConfig)}});
};

// 實際執行推送邏輯
const executePush = async () => {
    console.log('HiTeach 版本：', htVersion.value, '支援新 Push 功能：', isNewPushFuncVersion.value, 'iframe 環境：', isInIframe.value);

    if (isPushing.value) return;
    isPushing.value = true;

    try {
        if (shouldUseNewPushFunc.value) {
            // 新版本 HiTeach (>= 5.5.0.0002) 或 iframe 環境：創建活動 + 推送短網址
            console.log('[HTpvpGamePlay] 使用新版 Push 功能（創建活動 + 短網址）', isInIframe.value ? '(iframe 環境)' : '(HiTeach 版本支援)');

            // ✨ 修改：接收返回值
            const result = await createAndPushShortUrl(gameConfig, playerCount.value || 2); // PvP 遊戲 playerCount 使用 ref 值或預設 2

            // ✨ 新增：更新最新推送的活動 ID（覆蓋舊值）
            if (result.success && result.activityId) {
                pushedActivityId.value = result.activityId;
                pushedActivityName.value = gameConfig.topic || '摘星星遊戲';
                pushedShortUrl.value = result.shortUrl || '';
                // ✅ 同步更新 classRosterSource（根據 useHiTeachRoster 判斷）
                classRosterSource.value = gameConfig.useHiTeachRoster ? 'HT' : 'general';
                // 通知 HT 儲存當前遊戲設定與 restoredData（供翻頁恢復使用）
                messageBridge.postMessage({
                    action: 'Save.PvPGameSetting',
                    data: {
                        ...gameConfig,
                        restoredData: {
                            activityId: result.activityId,
                            shortUrl: result.shortUrl
                        }
                    }
                });
                console.log('[HTpvpGamePlay] 已更新最新推送活動 ID:', result.activityId);
                console.log('[HTpvpGamePlay] 已更新 classRosterSource:', classRosterSource.value);
                console.log('[HTpvpGamePlay] ⚠️ 注意：這會覆蓋之前的 activityId');
            }
        } else {
            // 舊版本 HiTeach 或無版本資訊：推送完整 JSON（維持現有邏輯）
            console.log('[HTpvpGamePlay] 使用舊版 Push 功能（推送 JSON）');
            sendToWebIRS();
            iviewMessage.success({
                content: t('PvPgame.sendSuccess'),
                duration: 3,
                closable: true
            });
            // 注意：舊版本無法取得 activityId，查看成績按鈕不會顯示
        }
    } catch (error) {
        console.error('[HTpvpGamePlay] Push 失敗:', error);
        // 錯誤訊息已在 createAndPushShortUrl 中顯示
    }

    isPushing.value = false;
};

// 重新推送目前的短網址（不重新上傳）
const repush = () => {
    if (!pushedShortUrl.value || !pushedActivityId.value) return;
    const originalUrl = `${window.location.origin}/ht-pvpgame-play?actId=${pushedActivityId.value}`;
    messageBridge.postMessage({
        action: 'SendGameLink.WebIRS',
        data: {
            url: originalUrl,
            shortUrl: pushedShortUrl.value,
            activityId: pushedActivityId.value,
            gameType: 'pvpgame'
        }
    });
    iviewMessage.success({
        content: t('Game.pushSuccess') || '重新推送成功',
        duration: 2,
        closable: true
    });
};

// 處理推送按鈕點擊
const handleSendClick = () => {
    // 非校園版：跳過確認彈窗，直接執行舊版推送
    if (!schoolVers.value) {
        executePush();
        return;
    }

    showPushConfirm.value = true;
};

// ✨ 開啟成績查看面板
const openScoresPanel = () => {
    if (!pushedActivityId.value) {
        iviewMessage.warning({
            content: t('HTwbActKitHome.noActivityPushed') || '尚未推送活動，無法查看成績',
            duration: 3,
            closable: true
        });
        return;
    }
    showScoresPanel.value = true;
};

// ✨ 關閉成績查看面板
const closeScoresPanel = () => {
    showScoresPanel.value = false;
};

// 複製短網址
const copyPushedShortUrl = () => {
    if (!pushedShortUrl.value) return;
    navigator.clipboard.writeText(pushedShortUrl.value).then(() => {
        iviewMessage.success({ content: t('gameActManager.shortUrlCopied') || '已複製連結', duration: 2 });
    }).catch(() => {
        iviewMessage.error({ content: t('gameActManager.copyFailed') || '複製失敗', duration: 2 });
    });
};

// 顯示 QR Code
const showPushedQRCode = async () => {
    showQRCodeOverlay.value = true;
    await nextTick();
    if (!qrcodeCanvas.value || !pushedShortUrl.value) return;
    try {
        await QRCode.toCanvas(qrcodeCanvas.value, pushedShortUrl.value, {
            width: 280,
            margin: 2,
            color: { dark: '#000000', light: '#FFFFFF' }
        });
    } catch (error) {
        console.error('生成 QR Code 失敗:', error);
    }
};

const closeQRCodeOverlay = () => {
    showQRCodeOverlay.value = false;
};

// 開啟遊戲雲端管理頁面
const openCloudManagement = () => {
    router.push('/game-act-manager');
};

// 載入預覽模式參數
const loadPreviewMode = (params) => {
  console.log('🎮 [摘星星預覽模式] 收到的完整參數:', params);
  isPreviewMode.value = true;
  playerCount.value = params.playerCount || 2;

  // 從 params.gameParameters 提取遊戲參數
  const gp = params.gameParameters || {};
  console.log('📋 [摘星星預覽模式] gameParameters:', gp);
  console.log('📝 [摘星星預覽模式] topic:', gp.topic);
  console.log('📝 [摘星星預覽模式] vocabularyList 數量:', gp.vocabularyList?.length || 0);
  console.log('❌ [摘星星預覽模式] errorVocabularyList 數量:', gp.errorVocabularyList?.length || 0);

  // 映射到 gameConfig 格式
  Object.assign(gameConfig, {
    topic: gp.topic || 'Preview',
    correctVocabularyList: gp.vocabularyList || [],
    errorVocabularyList: gp.errorVocabularyList || [],
    gameDuration: gp.gameDuration || 40,
    background: gp.background || 'ocean',
    gameSpeed: gp.gameSpeed || 2
  });

  console.log('✅ [摘星星預覽模式] 最終 gameConfig:', gameConfig);
  timeRemaining.value = gameConfig.gameDuration;

  // 顯示預覽模式提示（使用 nextTick 確保 Message 組件已完全初始化）
  nextTick(() => {
    try {
      if (iviewMessage && typeof iviewMessage.info === 'function') {
        iviewMessage.info({
          content: t('gameActManager.previewRefreshWarning'),
          duration: 5,
          closable: true
        });
      } else {
        console.warn('[摘星星預覽模式] iviewMessage 未正確注入，使用 console 提示');
        console.info(`📢 ${t('gameActManager.previewRefreshWarning')}`);
      }
    } catch (error) {
      console.error('[摘星星預覽模式] 顯示提示訊息失敗:', error);
      console.info(`📢 ${t('gameActManager.previewRefreshWarning')}`);
    }
  });
};

// 组件卸载时清除定时器
onUnmounted(() => {
  clearInterval(gameLoop);
  clearInterval(timerInterval);

  // 清理觸控狀態
  processedTouches.value.clear();

  // 移除 messageBridge 監聽器
  if (messageHandlerRef) {
    messageBridge.removeEventListener(messageHandlerRef);
    messageHandlerRef = null;
  }

  // 停止並釋放背景音樂
  if (backgroundMusic.value) {
    backgroundMusic.value.pause();
    backgroundMusic.value.src = '';
    backgroundMusic.value = null;
  }

  // 移除視窗大小調整監聽器
  window.removeEventListener('resize', handleResize);
  
  // 釋放正確和錯誤音效
  if (correctSound.value) {
    correctSound.value.src = '';
    correctSound.value = null;
  }
  
  if (wrongSound.value) {
    wrongSound.value.src = '';
    wrongSound.value = null;
  }
  
  // 釋放平局和獲勝音效
  if (tieSound.value) {
    tieSound.value.src = '';
    tieSound.value = null;
  }
  
  if (winSound.value) {
    winSound.value.src = '';
    winSound.value = null;
  }
  
  // 釋放 Ready 和 Go 音效
  if (readySound.value) {
    readySound.value.src = '';
    readySound.value = null;
  }
  
  if (goSound.value) {
    goSound.value.src = '';
    goSound.value = null;
  }
});
</script>

<style lang="less" scoped>
@import '../assets/HTwbActKitCSS/HTpvpGame.less';

// 推送按鈕樣式
.push-button {
    width: 60px;
    height: 60px;
    background-color: #2196F3; /* 藍色 */
    border: none;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    transition: transform 0.2s, background-color 0.2s;
    
    .sendweb-icon{
      width: 30px !important;
      height: 30px !important;
        *{
            fill: white !important;
        }
    }

    &:hover:not(.disabled) {
        transform: scale(1.1);
        background-color: #1976D2;
    }
}

.push-button.disabled {
    background-color: #9e9e9e !important; /* 灰色，使用 !important 確保優先級 */
    cursor: not-allowed !important;

    .sendweb-icon {
        * {
            fill: #666666 !important;
        }
    }
}

/* 預覽標記樣式（仿照打地鼠：黑底白字，右下角） */
.preview-badge {
  position: absolute;
  bottom: 20px;
  right: 20px;
  display: inline-block;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  font-size: 1rem;
  font-weight: bold;
  padding: 8px 16px;
  border-radius: 8px;
  letter-spacing: 1px;
  z-index: 100;
}

/* 單人模式：分數右上角顯示（與雙人模式右側分數樣式一致） */
.center-score {
  position: absolute;
  top: 0px;
  right: 0px;
  font-size: 2rem;
  font-weight: bold;
  color: white;
  z-index: 1;
  background-color: rgba(0, 0, 0, 0.5);
  border-top-right-radius: 10px;
  border-bottom-left-radius: 10px;
  padding: 5px 10px;
}

/* 單人模式：遊戲區域全螢幕 */
.game-side.single-player-mode {
  width: 100% !important;
  left: 0 !important;
}

/* 單人模式：結算卡片置中 */
.game-result-container.single-player-result {
  justify-content: center !important;
}

.game-result-container.single-player-result .result-card-wrap {
  max-width: 400px;
}

/* 單人模式：遊戲結束標題樣式（完全仿照打地鼠，不突出卡片外） */
.single-player-title {
  font-size: 24px;
  font-weight: bold;
  padding: 5px 15px;
  border-radius: 5px;
  display: inline-block;
  position: relative;
  top: 0px !important;
  background-color: transparent !important;
  color: #8b5d33;
  box-shadow: none !important;
}

.single-player-title::before,
.single-player-title::after {
  display: none !important;
}

/* 正式活動模式：外層容器（包含彈窗和切換遊戲區塊） */
.actid-mode-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0;
  width: auto;
  max-width: 90vw;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow-y: auto !important;
  overflow-x: hidden !important;
  -webkit-overflow-scrolling: touch; /* iOS 平滑捲動 */

  /* 雙人模式：三欄佈局 */
  &.dual-player-layout {
    max-width: 95vw;
  }

  /* 小螢幕時設定最大高度並啟用捲動 */
  @media (max-width: 1200px) {
    max-width: 95vw;
    max-height: calc(100vh - 140px) !important; /* 扣除警告遮罩和邊距 */
  }

  @media (max-width: 768px) {
    max-height: calc(100vh - 120px) !important;
  }

  @media (max-width: 600px) {
    max-height: calc(100vh - 100px) !important;
  }

  @media (max-width: 480px) {
    max-height: calc(100vh - 90px) !important;
  }

  /* 自訂捲動條樣式（WebKit） */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.4);
    border-radius: 4px;

    &:hover {
      background: rgba(0, 0, 0, 0.6);
    }
  }
}

/* 正式活動模式：兩欄或三欄佈局 */
.actid-mode-modal {
  display: flex;
  align-items: stretch; // 確保所有子元素等高
  gap: 0;
  width: 100%;
  background: transparent;
  border-radius: 12px 12px 0 0;
  overflow: visible;
  flex-shrink: 0;

  /* 小螢幕時改為垂直佈局 */
  @media (max-width: 768px) {
    flex-direction: column;
  }
}

/* 左側：左側玩家資訊（僅雙人模式） */
.student-info-left {
  min-width: 240px;
  min-height: 250px; // 確保最小高度一致
  padding: 30px 24px;
  background: rgba(255, 240, 245, 0.98);
  border-radius: 12px 0 0 12px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start; // 從頂部對齊，確保高度一致
  flex-shrink: 0;

  /* 小螢幕時改為全寬 */
  @media (max-width: 768px) {
    min-width: 100%;
    width: 100%;
    border-radius: 12px 12px 0 0;
    padding: 20px 16px;
  }

  .student-info-title {
    font-size: 16px;
    font-weight: bold;
    margin-bottom: 16px;
    text-align: center;
    color: #d81b60;
  }

  .form-group {
    margin-bottom: 12px;

    .student-input {
      width: 100%;
      padding: 10px 12px;
      font-size: 14px;
      border: 2px solid #ddd;
      border-radius: 8px;
      transition: border-color 0.2s;
      box-sizing: border-box;

      &:focus {
        outline: none;
        border-color: #d81b60;
      }

      &::placeholder {
        color: #999;
      }
    }

    .student-select {
      cursor: pointer;
      background-color: white;
      appearance: none;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 14px center;
      padding-right: 40px;

      &:hover {
        border-color: #d81b60;
      }
    }

    .error-text {
      display: block;
      margin-top: 4px;
      font-size: 11px;
      color: #ff4d4f;
    }
  }

  // 已選擇資訊顯示區塊
  .selected-info {
    margin-top: 16px;
    padding: 14px;
    background: rgba(216, 27, 96, 0.08);
    border-radius: 8px;
    border: 1px solid rgba(216, 27, 96, 0.3);

    .info-item {
      display: flex;
      align-items: center;
      margin-bottom: 8px;
      font-size: 14px;

      &:last-child {
        margin-bottom: 0;
      }

      .info-label {
        font-weight: bold;
        color: #333;
        margin-right: 8px;
        min-width: 60px;
      }

      .info-value {
        color: #d81b60;
        font-weight: 600;
      }
    }
  }
}

/* 中間：遊戲資訊和播放按鈕 */
.game-info-center {
  min-width: 280px;
  min-height: 250px; // 確保與左右兩側高度一致
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 30px 24px;
  background: rgba(255, 249, 235, 0.98);
  flex-shrink: 0;

  /* 小螢幕時改為全寬 */
  @media (max-width: 768px) {
    min-width: 100%;
    width: 100%;
    border-radius: 0;
    padding: 20px 16px;
  }

  .game-title {
    font-size: 24px;
    font-weight: bold;
    margin-bottom: 20px;
    padding: 0 12px;
    color: #8B4513;
    text-align: center;
    white-space: nowrap;
    background-color: transparent;
  }

  .game-info {
    display: flex;
    flex-direction: column;
    gap: 12px;
    font-size: 14px;
    color: #333;
    width: 100%;
    margin-bottom: 8px;

    .game-time, .game-speed {
      padding: 8px 12px;
      background: rgba(255, 255, 255, 0.8);
      border-radius: 6px;
      text-align: center;
      white-space: nowrap;
      font-weight: bold;
      font-size: 16px;
      color: #333;
    }
  }

  .play-in-center {
    margin-top: 8px;
  }

  /* 按鈕區：橫向排列 */
  .center-buttons {
    display: flex;
    gap: 12px;
    justify-content: center;
    align-items: center;
    margin-top: 8px;

    .play-button,
    .layout-toggle-button {
      margin: 0;
    }
  }
}

/* 右側：右側玩家資訊 */
.student-info-right {
  min-width: 280px;
  min-height: 250px; // 確保最小高度一致
  padding: 30px 28px;
  background: rgba(255, 255, 255, 0.98);
  border-radius: 0 12px 12px 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start; // 從頂部對齊，確保高度一致
  flex-shrink: 0;

  /* 小螢幕時改為全寬 */
  @media (max-width: 768px) {
    min-width: 100%;
    width: 100%;
    border-radius: 0 0 12px 12px;
    padding: 20px 16px;
  }

  .student-info-title {
    font-size: 17px;
    font-weight: bold;
    margin-bottom: 18px;
    text-align: center;
    color: #333;
  }

  .form-group {
    margin-bottom: 14px;

    .student-input {
      width: 100%;
      padding: 11px 14px;
      font-size: 15px;
      border: 2px solid #ddd;
      border-radius: 8px;
      transition: border-color 0.2s;
      box-sizing: border-box;

      &:focus {
        outline: none;
        border-color: #4CAF50;
      }

      &::placeholder {
        color: #999;
      }
    }

    .student-select {
      cursor: pointer;
      background-color: white;
      appearance: none;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 14px center;
      padding-right: 40px;

      &:hover {
        border-color: #4CAF50;
      }
    }

    .error-text {
      display: block;
      margin-top: 4px;
      font-size: 11px;
      color: #ff4d4f;
    }
  }

  // 已選擇資訊顯示區塊
  .selected-info {
    margin-top: 16px;
    padding: 14px;
    background: rgba(76, 175, 80, 0.08);
    border-radius: 8px;
    border: 1px solid rgba(76, 175, 80, 0.3);

    .info-item {
      display: flex;
      align-items: center;
      margin-bottom: 8px;
      font-size: 14px;

      &:last-child {
        margin-bottom: 0;
      }

      .info-label {
        font-weight: bold;
        color: #333;
        margin-right: 8px;
        min-width: 60px;
      }

      .info-value {
        color: #4CAF50;
        font-weight: 600;
      }
    }
  }
}

/* 小螢幕警告遮罩 */
.small-screen-warning {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.85);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  animation: slideDown 0.3s ease-out;

  .warning-content {
    display: flex;
    align-items: center;
    padding: 14px 20px;
    gap: 12px;
    max-width: 1200px;
    margin: 0 auto;

    .warning-icon {
      width: 20px;
      height: 20px;
      color: white;
      flex-shrink: 0;
    }

    .warning-text {
      flex: 1;
      color: white;
      font-size: 13px;
      line-height: 1.5;
      margin: 0;
    }

    .warning-close {
      width: 28px;
      height: 28px;
      border: none;
      background: rgba(255, 255, 255, 0.15);
      border-radius: 4px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s;
      flex-shrink: 0;

      &:hover {
        background: rgba(255, 255, 255, 0.25);
      }

      svg {
        width: 14px;
        height: 14px;
        color: white;
      }
    }
  }
}

@keyframes slideDown {
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* 單人模式：右側資訊區樣式（與打地鼠一致） */
.actid-mode-modal:not(.dual-player-layout) .student-info-right {
  .student-info-title {
    color: #333;
  }

  .student-input:focus {
    border-color: #4CAF50;
  }

  /* 單人模式在小螢幕時上部不用圓角 */
  @media (max-width: 768px) {
    border-radius: 0;
  }
}

/* 遊戲模板切換按鈕（在播放按鈕旁邊） */
.layout-toggle-button {
  width: 60px;
  height: 60px;
  background-color: #FF9800;
  border: none;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.2s;
  flex-shrink: 0;

  &:hover {
    background-color: #FB8C00;
  }

  .change-icon {
    width: 28px;
    height: 28px;
    color: white;

    ::v-deep svg {
      fill: white !important;

      * {
        fill: white !important;
      }
    }
  }
}

/* 切換遊戲模板區塊 */
.game-template-switcher {
  width: 100%;
  padding: 20px;
  background: rgba(248, 249, 250, 0.98);
  border-radius: 12px;
  margin-top: 15px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  .switcher-title {
    font-size: 16px;
    font-weight: bold;
    margin-bottom: 12px;
    color: #333;
    text-align: center;
  }

  .switcher-content {
    display: flex;
    gap: 12px;
    align-items: center;
    justify-content: center;

    .template-select {
      flex: 1;
      max-width: 400px;
      padding: 10px 14px;
      font-size: 15px;
      border: 2px solid #ddd;
      border-radius: 8px;
      background-color: white;
      cursor: pointer;
      transition: border-color 0.2s;

      &:focus {
        outline: none;
        border-color: #FF9800;
      }
    }

    .goto-button {
      width: 48px;
      height: 48px;
      background-color: #FF9800;
      border: none;
      border-radius: 50%;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      transition: all 0.2s;
      flex-shrink: 0;

      &:hover:not(:disabled) {
        background-color: #F57C00;
        transform: scale(1.05);
      }

      &:disabled {
        background-color: #ccc;
        cursor: not-allowed;
        opacity: 0.6;
      }

      svg {
        width: 24px;
        height: 24px;
        color: white;
      }
    }
  }
}
</style>
